import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getValidToken, API_URL } from './utils/authUtils';
import { devLog, devError } from './utils/secureLogger';
import AdminRouteGuard from './components/AdminRouteGuard';
import { ADMIN_SECTIONS } from './utils/adminPermissions';
import {
  ResponsiveTable,
  MobileStudentCard,
  useDeviceDetect
} from './components/ResponsiveComponents';

const BACKEND_URL = API_URL;

const AdminStudentRecords = () => {
  const navigate = useNavigate();
  const { isMobile } = useDeviceDetect();

  // State management
  const [students, setStudents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalStudents, setTotalStudents] = useState(0);
  const [limit, setLimit] = useState(50);

  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [universityFilter, setUniversityFilter] = useState('');
  const [majorFilter, setMajorFilter] = useState('');
  const [minGpa, setMinGpa] = useState('');
  const [maxGpa, setMaxGpa] = useState('');
  const [classYearFilter, setClassYearFilter] = useState('');
  const [verificationFilter, setVerificationFilter] = useState('');
  const [appliedFilter, setAppliedFilter] = useState('');

  // Available filter options (will be populated from data)
  const [universities, setUniversities] = useState([]);
  const [majors, setMajors] = useState([]);
  const [classYears, setClassYears] = useState([]);

  // CSV Upload state
  const [showAddNewStudents, setShowAddNewStudents] = useState(false);
  const [newStudentFile, setNewStudentFile] = useState(null);
  const [newStudentUploadLoading, setNewStudentUploadLoading] = useState(false);
  const [newStudentUploadResults, setNewStudentUploadResults] = useState(null);
  const [showNewStudentResults, setShowNewStudentResults] = useState(false);

  // Single student add form state
  const [showAddSingleStudent, setShowAddSingleStudent] = useState(false);
  const [singleStudentForm, setSingleStudentForm] = useState({
    first_name: '',
    last_name: '',
    university_email: '',
    current_university_verified: '',
    major: '',
    expected_grad_date: '',
    current_academic_class: '',
    gpa: '',
    matriculation_date: '',
    conferred_grad_date: '',
    date_of_initial_block_term: '',
    major_at_matriculation: '',
    applicant_status: '',
    citizenship_status: '',
    gender: '',
    race: '',
    block_grant_scholar: '',
    corporate_scholar: '',
    special_scholarship: '',
    nvb_scholar: ''
  });
  const [singleStudentLoading, setSingleStudentLoading] = useState(false);
  const [singleStudentError, setSingleStudentError] = useState(null);
  const [singleStudentSuccess, setSingleStudentSuccess] = useState(null);

  // Fetch students data
  const fetchStudents = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const token = getValidToken();
      if (!token) {
        navigate('/login');
        return;
      }

      // Build query parameters
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: limit.toString()
      });

      if (searchTerm.trim()) params.append('search', searchTerm.trim());
      if (universityFilter) params.append('university', universityFilter);
      if (majorFilter) params.append('major', majorFilter);
      if (minGpa) params.append('min_gpa', minGpa);
      if (maxGpa) params.append('max_gpa', maxGpa);
      if (classYearFilter) params.append('class_year', classYearFilter);
      if (verificationFilter) params.append('verification_status', verificationFilter);
      if (appliedFilter) params.append('has_applied', appliedFilter);

      const response = await fetch(`${BACKEND_URL}/api/admin/students?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch students: ${response.statusText}`);
      }

      const data = await response.json();

      setStudents(data.students || []);
      setTotalPages(data.pagination?.pages || 1);
      setTotalStudents(data.pagination?.total || 0);

      devLog('Fetched students:', data);
    } catch (err) {
      devError('Error fetching students:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch filter options
  const fetchFilterOptions = async () => {
    try {
      const token = getValidToken();
      if (!token) return;

      // This would ideally be a separate endpoint, but for now we can extract from the data
      // In a future enhancement, create /api/admin/students/filter-options endpoint

    } catch (err) {
      devError('Error fetching filter options:', err);
    }
  };

  // Initial load
  useEffect(() => {
    fetchStudents();
    fetchFilterOptions();
  }, [currentPage, limit]);

  // Handle search/filter submission
  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1); // Reset to first page
    fetchStudents();
  };

  // Handle clear filters
  const handleClearFilters = () => {
    setSearchTerm('');
    setUniversityFilter('');
    setMajorFilter('');
    setMinGpa('');
    setMaxGpa('');
    setClassYearFilter('');
    setVerificationFilter('');
    setAppliedFilter('');
    setCurrentPage(1);
    fetchStudents();
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // CSV Upload handlers
  const handleNewStudentFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
        setError('Please select a CSV file');
        return;
      }
      setNewStudentFile(file);
      setError('');
    }
  };

  const downloadNewStudentTemplate = async () => {
    const token = getValidToken();

    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/new-students-template`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to download template');
      }

      // Create download link
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Get filename from response headers or use default
      const contentDisposition = response.headers.get('content-disposition');
      const filename = contentDisposition
        ? contentDisposition.split('filename=')[1].replace(/"/g, '')
        : `new_students_template_admin_${new Date().toISOString().split('T')[0]}.csv`;

      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading new student template:', err);
      setError('Failed to download template. Please try again.');
    }
  };

  const submitSingleStudent = async (e) => {
    e.preventDefault();
    setSingleStudentLoading(true);
    setSingleStudentError(null);
    setSingleStudentSuccess(null);

    try {
      const token = getValidToken();
      if (!token) {
        navigate('/login');
        return;
      }

      // Create CSV string from form data
      const headers = Object.keys(singleStudentForm);
      const values = Object.values(singleStudentForm);
      const csvContent = `${headers.join(',')}\n${values.join(',')}`;

      // Create a blob and file object
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const file = new File([blob], 'single_student.csv', { type: 'text/csv' });

      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${BACKEND_URL}/api/admin/add-students-csv`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to add student');
      }

      const data = await response.json();

      if (data.failed && data.failed.length > 0) {
        setSingleStudentError(data.failed[0].errors.join(', '));
      } else if (data.successful && data.successful.length > 0) {
        setSingleStudentSuccess(`Student added successfully! ID: ${data.successful[0].student_id}`);
        // Reset form
        setSingleStudentForm({
          first_name: '',
          last_name: '',
          university_email: '',
          current_university_verified: '',
          major: '',
          expected_grad_date: '',
          current_academic_class: '',
          gpa: '',
          matriculation_date: '',
          conferred_grad_date: '',
          date_of_initial_block_term: '',
          major_at_matriculation: '',
          applicant_status: '',
          citizenship_status: '',
          gender: '',
          race: '',
          block_grant_scholar: '',
          corporate_scholar: '',
          special_scholarship: '',
          nvb_scholar: ''
        });
        // Refresh student list
        fetchStudents();

        // Auto-hide success message after 5 seconds
        setTimeout(() => {
          setSingleStudentSuccess(null);
        }, 5000);
      }
    } catch (err) {
      console.error('Error adding single student:', err);
      setSingleStudentError(err.message || 'Failed to add student');
    } finally {
      setSingleStudentLoading(false);
    }
  };

  const uploadNewStudentsCSV = async () => {
    if (!newStudentFile) {
      setError('Please select a CSV file first');
      return;
    }

    const token = getValidToken();
    setNewStudentUploadLoading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', newStudentFile);

      const response = await fetch(`${BACKEND_URL}/api/admin/add-students-csv`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to upload CSV');
      }

      // Handle upload response
      const data = await response.json();
      console.log('Upload response:', data);
      setNewStudentUploadResults(data);
      setShowNewStudentResults(true);
      setNewStudentFile(null);

      // Reset file input
      const fileInput = document.getElementById('new-student-csv-file-input');
      if (fileInput) fileInput.value = '';

      // Refresh student list if there were successful uploads
      if (data.successful && data.successful.length > 0) {
        fetchStudents();
      }

    } catch (err) {
      console.error('Error uploading new students CSV:', err);
      setError(err.message || 'Failed to upload CSV. Please try again.');
    } finally {
      setNewStudentUploadLoading(false);
    }
  };

  // Render mobile card for a student
  const renderMobileStudentCard = (student) => (
    <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-md">
      <div className="space-y-3">
        {/* Student Name and ID */}
        <div className="border-b border-gray-200 dark:border-gray-700 pb-2">
          <h3
            className="text-lg font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 cursor-pointer"
            onClick={() => navigate(`/admin/students/${student.id}`)}
          >
            {student.first_name} {student.last_name}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">ID: {student.id}</p>
        </div>

        {/* Contact Information */}
        <div className="space-y-1">
          <div className="flex justify-between text-sm">
            <span className="font-medium text-gray-600 dark:text-gray-400">Email:</span>
            <span className="text-gray-900 dark:text-white">{student.email}</span>
          </div>
        </div>

        {/* Academic Information */}
        <div className="space-y-1">
          <div className="flex justify-between text-sm">
            <span className="font-medium text-gray-600 dark:text-gray-400">University:</span>
            <span className="text-gray-900 dark:text-white">{student.university || 'N/A'}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="font-medium text-gray-600 dark:text-gray-400">Major:</span>
            <span className="text-gray-900 dark:text-white">{student.major || 'N/A'}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="font-medium text-gray-600 dark:text-gray-400">GPA:</span>
            <span className="text-gray-900 dark:text-white">{student.gpa || 'N/A'}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="font-medium text-gray-600 dark:text-gray-400">Class Year:</span>
            <span className="text-gray-900 dark:text-white">{student.class_year || 'N/A'}</span>
          </div>
        </div>

        {/* Status Information */}
        <div className="space-y-1">
          <div className="flex justify-between text-sm">
            <span className="font-medium text-gray-600 dark:text-gray-400">Verified:</span>
            <span className={`font-semibold ${student.is_verified ? 'text-green-600' : 'text-yellow-600'}`}>
              {student.is_verified ? 'Yes' : 'No'}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="font-medium text-gray-600 dark:text-gray-400">Applied:</span>
            <span className={`font-semibold ${student.has_applied ? 'text-blue-600' : 'text-gray-600'}`}>
              {student.has_applied ? 'Yes' : 'No'}
            </span>
          </div>
        </div>

        {/* Registration Date */}
        <div className="text-xs text-gray-500 dark:text-gray-400 pt-2 border-t border-gray-200 dark:border-gray-700">
          Registered: {new Date(student.created_at).toLocaleDateString()}
        </div>
      </div>
    </div>
  );

  if (isLoading && students.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading students...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center text-red-600">
          <p className="text-xl font-semibold">Error</p>
          <p className="mt-2">{error}</p>
          <button
            onClick={fetchStudents}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <AdminRouteGuard requiredPermission={ADMIN_SECTIONS.STUDENT_RECORDS}>
      <div className="container mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Student Records
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            View and manage all student profiles ({totalStudents} total)
          </p>
        </div>
        <button
          onClick={() => navigate('/admin')}
          className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
          </svg>
          Back to Admin
        </button>
      </div>

      {/* Add New Students CSV Upload Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6">
        <button
          type="button"
          onClick={() => setShowAddNewStudents(!showAddNewStudents)}
          className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        >
          <div className="flex items-center gap-3">
            <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Add New Students (CSV Upload)
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Bulk import new student records via CSV file
              </p>
            </div>
          </div>
          <svg
            className={`w-5 h-5 text-gray-400 transition-transform ${showAddNewStudents ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {showAddNewStudents && (
          <div className="px-6 pb-6 border-t border-gray-200 dark:border-gray-700">
            <div className="mt-6 space-y-4">
              {/* Step 1: Download Template */}
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">
                  Step 1: Download CSV Template
                </h3>
                <p className="text-sm text-blue-800 dark:text-blue-200 mb-3">
                  Download the template, fill in student information, and save as CSV format.
                </p>
                <button
                  type="button"
                  onClick={downloadNewStudentTemplate}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Download Template
                </button>
              </div>

              {/* Validation Rules */}
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">
                  CSV Format Guidelines & Validation Rules
                </h3>
                <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1 list-disc list-inside">
                  <li><strong>Required Fields:</strong> first_name, last_name, university_email, current_university_verified</li>
                  <li><strong>Student ID:</strong> Leave BLANK for new students (system will generate)</li>
                  <li><strong>Date Fields (YYYY-MM-DD format):</strong> matriculation_date, expected_grad_date, conferred_grad_date, date_of_initial_block_term</li>
                  <li><strong>GPA:</strong> Decimal 0.00-4.00 or leave blank</li>
                  <li><strong>Scholarship Fields (Y/N only):</strong> block_grant_scholar, corporate_scholar, special_scholarship, nvb_scholar</li>
                  <li><strong>Classification:</strong> College - 1st Year, College - 2nd Year, College - 3rd Year, College - 4th Year, College - 5th Year, NA</li>
                  <li><strong>Citizenship:</strong> US CITIZEN, NON US CITIZEN (PERMANENT RESIDENT), NON US CITIZEN (PERMIT), NON US CITIZEN (OTHER)</li>
                  <li><strong>Gender:</strong> Male, Female, Nonconforming, Transgender Male, Transgender Female, Non-Binary, NonDisclose</li>
                  <li><strong>Race:</strong> AA/BLACK, AL/ALASKANAT, ASIAN, LATINx/o/a, NonDisclose, PI/NatHawaii, WHITE, MULTI-RACIAL</li>
                  <li><strong>Status:</strong> Graduated, Continuing, Health Leave of Absence, Co-Op Leave of Absence, Temporary Leave Status, Disqualification Withdrawal, University Withdrawal, External Transfer, Permanent Leave</li>
                  <li><strong>Major Fields:</strong> Use proper capitalization (e.g., "Biomedical Engineering")</li>
                </ul>
              </div>

              {/* Step 2: Upload File */}
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                <h3 className="font-semibold text-green-900 dark:text-green-100 mb-2">
                  Step 2: Upload Completed CSV
                </h3>
                <div className="space-y-3">
                  <div>
                    <input
                      id="new-student-csv-file-input"
                      type="file"
                      accept=".csv"
                      onChange={handleNewStudentFileSelect}
                      className="block w-full text-sm text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer bg-gray-50 dark:bg-gray-700 focus:outline-none"
                    />
                    {newStudentFile && (
                      <p className="mt-2 text-sm text-green-600 dark:text-green-400">
                        Selected: {newStudentFile.name}
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={uploadNewStudentsCSV}
                    disabled={!newStudentFile || newStudentUploadLoading}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {newStudentUploadLoading ? (
                      <>
                        <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Uploading...
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        Upload CSV
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Important Note */}
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                <div className="flex gap-3">
                  <svg className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <div>
                    <h4 className="font-semibold text-yellow-900 dark:text-yellow-100 mb-1">
                      Admin Direct Import
                    </h4>
                    <p className="text-sm text-yellow-800 dark:text-yellow-200">
                      Students uploaded via this admin interface are <strong>automatically verified</strong> and immediately active in the system. Unlike partner uploads which require admin approval, these records are created directly with verified status.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Add Single Student Form */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6">
        <button
          type="button"
          onClick={() => setShowAddSingleStudent(!showAddSingleStudent)}
          className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        >
          <div className="flex items-center gap-3">
            <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Add Single Student (Manual Entry)
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Add one student record at a time using a form
              </p>
            </div>
          </div>
          <svg
            className={`w-5 h-5 text-gray-400 transition-transform ${showAddSingleStudent ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {showAddSingleStudent && (
          <div className="px-6 pb-6 border-t border-gray-200 dark:border-gray-700">
            <form onSubmit={submitSingleStudent} className="mt-6 space-y-6">
              {/* Success Message */}
              {singleStudentSuccess && (
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-800 dark:text-green-200 px-4 py-3 rounded">
                  {singleStudentSuccess}
                </div>
              )}

              {/* Error Message */}
              {singleStudentError && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-200 px-4 py-3 rounded">
                  {singleStudentError}
                </div>
              )}

              {/* Required Fields Section */}
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-4">Required Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      First Name <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={singleStudentForm.first_name}
                      onChange={(e) => setSingleStudentForm({...singleStudentForm, first_name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Last Name <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={singleStudentForm.last_name}
                      onChange={(e) => setSingleStudentForm({...singleStudentForm, last_name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      University Email <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="email"
                      required
                      value={singleStudentForm.university_email}
                      onChange={(e) => setSingleStudentForm({...singleStudentForm, university_email: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Current University <span className="text-red-600">*</span>
                    </label>
                    <select
                      required
                      value={singleStudentForm.current_university_verified}
                      onChange={(e) => setSingleStudentForm({...singleStudentForm, current_university_verified: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="">Select University...</option>
                      <option value="Abilene Christian University">Abilene Christian University</option>
                      <option value="Academy of Art College">Academy of Art College</option>
                      <option value="Adams State College">Adams State College</option>
                      <option value="Adelphi University">Adelphi University</option>
                      <option value="Alabama Agricultural and Mechanical University">Alabama Agricultural and Mechanical University</option>
                      <option value="Alabama State University">Alabama State University</option>
                      <option value="Albany State University">Albany State University</option>
                      <option value="Alcorn State University">Alcorn State University</option>
                      <option value="Alfred University">Alfred University</option>
                      <option value="Allegheny College">Allegheny College</option>
                      <option value="Allen University">Allen University</option>
                      <option value="American University">American University</option>
                      <option value="Amherst College">Amherst College</option>
                      <option value="Andrews University">Andrews University</option>
                      <option value="Angelo State University">Angelo State University</option>
                      <option value="Appalachian State University">Appalachian State University</option>
                      <option value="Arizona State University">Arizona State University</option>
                      <option value="Arkansas State University">Arkansas State University</option>
                      <option value="Arkansas Tech University">Arkansas Tech University</option>
                      <option value="Armstrong Atlantic State University">Armstrong Atlantic State University</option>
                      <option value="Auburn University">Auburn University</option>
                      <option value="Auburn University at Montgomery">Auburn University at Montgomery</option>
                      <option value="Augusta State University">Augusta State University</option>
                      <option value="Augsburg College">Augsburg College</option>
                      <option value="Austin Peay State University">Austin Peay State University</option>
                      <option value="Ave Maria University">Ave Maria University</option>
                      <option value="Azusa Pacific University">Azusa Pacific University</option>
                      <option value="Babson College">Babson College</option>
                      <option value="Baker University">Baker University</option>
                      <option value="Baldwin-Wallace College">Baldwin-Wallace College</option>
                      <option value="Ball State University">Ball State University</option>
                      <option value="Barnard College - Columbia University">Barnard College - Columbia University</option>
                      <option value="Barry University">Barry University</option>
                      <option value="Barton College">Barton College</option>
                      <option value="Bates College">Bates College</option>
                      <option value="Baylor University">Baylor University</option>
                      <option value="Belmont University">Belmont University</option>
                      <option value="Beloit College">Beloit College</option>
                      <option value="Bemidji State Univeristy">Bemidji State Univeristy</option>
                      <option value="Benedict College">Benedict College</option>
                      <option value="Benedictine College">Benedictine College</option>
                      <option value="Benedictine University">Benedictine University</option>
                      <option value="Bennett College">Bennett College</option>
                      <option value="Bennington College">Bennington College</option>
                      <option value="Bentley College">Bentley College</option>
                      <option value="Berea College">Berea College</option>
                      <option value="Berklee College of Music">Berklee College of Music</option>
                      <option value="Berry College">Berry College</option>
                      <option value="Bethune-Cookman College">Bethune-Cookman College</option>
                      <option value="Biola University">Biola University</option>
                      <option value="Birmingham-Southern College">Birmingham-Southern College</option>
                      <option value="Black Hills State University">Black Hills State University</option>
                      <option value="Bloomfield College">Bloomfield College</option>
                      <option value="Bloomsburg University of Pennsylvania">Bloomsburg University of Pennsylvania</option>
                      <option value="Bluefield State College">Bluefield State College</option>
                      <option value="Boise State University">Boise State University</option>
                      <option value="Boston College">Boston College</option>
                      <option value="Boston University">Boston University</option>
                      <option value="Bowdoin College">Bowdoin College</option>
                      <option value="Bowie State University">Bowie State University</option>
                      <option value="Bowling Green State University">Bowling Green State University</option>
                      <option value="Bradley University">Bradley University</option>
                      <option value="Brandeis University">Brandeis University</option>
                      <option value="Brandman University">Brandman University</option>
                      <option value="Brenau University">Brenau University</option>
                      <option value="Bridgewater State College">Bridgewater State College</option>
                      <option value="Brigham Young University">Brigham Young University</option>
                      <option value="Brown University">Brown University</option>
                      <option value="Bryant University">Bryant University</option>
                      <option value="Bryn Mawr College">Bryn Mawr College</option>
                      <option value="Bucknell University">Bucknell University</option>
                      <option value="Buena Vista University">Buena Vista University</option>
                      <option value="Butler University">Butler University</option>
                      <option value="California Institute of Technology">California Institute of Technology</option>
                      <option value="California Polytechnic State University - San Luis Obispo">California Polytechnic State University - San Luis Obispo</option>
                      <option value="California State Polytechnic University - Pomona">California State Polytechnic University - Pomona</option>
                      <option value="California State University, Bakersfield">California State University, Bakersfield</option>
                      <option value="California State University, Chico">California State University, Chico</option>
                      <option value="California State University, Dominguez Hills">California State University, Dominguez Hills</option>
                      <option value="California State University, Fresno">California State University, Fresno</option>
                      <option value="California State University, Fullerton">California State University, Fullerton</option>
                      <option value="California State University, Long Beach">California State University, Long Beach</option>
                      <option value="California State University, Los Angeles">California State University, Los Angeles</option>
                      <option value="California State University, Northridge">California State University, Northridge</option>
                      <option value="California State University, Sacramento">California State University, Sacramento</option>
                      <option value="California State University, San Bernadino">California State University, San Bernadino</option>
                      <option value="California University of Pennsylvania">California University of Pennsylvania</option>
                      <option value="Calvin College">Calvin College</option>
                      <option value="Cameron University">Cameron University</option>
                      <option value="Campbell University">Campbell University</option>
                      <option value="Canisius College">Canisius College</option>
                      <option value="Capella University">Capella University</option>
                      <option value="Capital University">Capital University</option>
                      <option value="Carleton College">Carleton College</option>
                      <option value="Carnegie Mellon University">Carnegie Mellon University</option>
                      <option value="Carroll College Helena">Carroll College Helena</option>
                      <option value="Carson-Newman College">Carson-Newman College</option>
                      <option value="Carthage College">Carthage College</option>
                      <option value="Case Western Reserve University">Case Western Reserve University</option>
                      <option value="Castleton State College">Castleton State College</option>
                      <option value="Catawba College">Catawba College</option>
                      <option value="Cedar Crest College">Cedar Crest College</option>
                      <option value="Cedarville College">Cedarville College</option>
                      <option value="Centenary College of Louisiana">Centenary College of Louisiana</option>
                      <option value="Central Connecticut State University">Central Connecticut State University</option>
                      <option value="Central Michigan University">Central Michigan University</option>
                      <option value="Central State University">Central State University</option>
                      <option value="Central Washington University">Central Washington University</option>
                      <option value="Centre College">Centre College</option>
                      <option value="Chadron State College">Chadron State College</option>
                      <option value="Chaminade University of Honolulu">Chaminade University of Honolulu</option>
                      <option value="Chapman University">Chapman University</option>
                      <option value="Charleston Southern University">Charleston Southern University</option>
                      <option value="Chatham College">Chatham College</option>
                      <option value="Cheyney University of Pennsylvania">Cheyney University of Pennsylvania</option>
                      <option value="Chicago State University">Chicago State University</option>
                      <option value="Christian Brothers University">Christian Brothers University</option>
                      <option value="Christopher Newport University">Christopher Newport University</option>
                      <option value="City University of New York (CUNY) System">City University of New York (CUNY) System</option>
                      <option value="Claflin College">Claflin College</option>
                      <option value="Claremont McKenna College">Claremont McKenna College</option>
                      <option value="Clarion University">Clarion University</option>
                      <option value="Clark Atlanta University">Clark Atlanta University</option>
                      <option value="Clarke College">Clarke College</option>
                      <option value="Clarkson University">Clarkson University</option>
                      <option value="Clark University">Clark University</option>
                      <option value="Clayton College & State University">Clayton College & State University</option>
                      <option value="Clemson University">Clemson University</option>
                      <option value="Cleveland State University">Cleveland State University</option>
                      <option value="Coastal Carolina University">Coastal Carolina University</option>
                      <option value="Coe College">Coe College</option>
                      <option value="Coker College">Coker College</option>
                      <option value="Colby College">Colby College</option>
                      <option value="Colgate University">Colgate University</option>
                      <option value="College for Creative Studies">College for Creative Studies</option>
                      <option value="College of Charleston">College of Charleston</option>
                      <option value="College of Idaho">College of Idaho</option>
                      <option value="College of New Rochelle">College of New Rochelle</option>
                      <option value="College of Notre Dame of Maryland">College of Notre Dame of Maryland</option>
                      <option value="College of St. Catherine">College of St. Catherine</option>
                      <option value="College of St. Rose">College of St. Rose</option>
                      <option value="College of the Holy Cross">College of the Holy Cross</option>
                      <option value="College of William and Mary">College of William and Mary</option>
                      <option value="Colorado Christian University">Colorado Christian University</option>
                      <option value="Colorado College">Colorado College</option>
                      <option value="Colorado School of Mines">Colorado School of Mines</option>
                      <option value="Colorado State University">Colorado State University</option>
                      <option value="Colorado State University-Pueblo">Colorado State University-Pueblo</option>
                      <option value="Colorado Technical University">Colorado Technical University</option>
                      <option value="Columbia College Chicago">Columbia College Chicago</option>
                      <option value="Columbia University">Columbia University</option>
                      <option value="Columbus State University">Columbus State University</option>
                      <option value="Community College of Denver">Community College of Denver</option>
                      <option value="Concordia College, Moorhead">Concordia College, Moorhead</option>
                      <option value="Connecticut College">Connecticut College</option>
                      <option value="Converse College">Converse College</option>
                      <option value="Coppin State College">Coppin State College</option>
                      <option value="Cornell College">Cornell College</option>
                      <option value="Cornell University">Cornell University</option>
                      <option value="Covenant College">Covenant College</option>
                      <option value="Creighton University">Creighton University</option>
                      <option value="Culver-Stockton College">Culver-Stockton College</option>
                      <option value="Cumberland University">Cumberland University</option>
                      <option value="Curry College">Curry College</option>
                      <option value="Daemen College">Daemen College</option>
                      <option value="Dakota State University">Dakota State University</option>
                      <option value="Dakota Wesleyan University">Dakota Wesleyan University</option>
                      <option value="Dallas Baptist University">Dallas Baptist University</option>
                      <option value="Dartmouth College">Dartmouth College</option>
                      <option value="Davidson College">Davidson College</option>
                      <option value="Delaware State University">Delaware State University</option>
                      <option value="Delaware Valley College">Delaware Valley College</option>
                      <option value="Delta State University">Delta State University</option>
                      <option value="Denison University">Denison University</option>
                      <option value="DePaul University">DePaul University</option>
                      <option value="DePauw University">DePauw University</option>
                      <option value="DeSales University">DeSales University</option>
                      <option value="DeVry Institute of Technology, Chicago">DeVry Institute of Technology, Chicago</option>
                      <option value="Dickinson College">Dickinson College</option>
                      <option value="Dickinson State University">Dickinson State University</option>
                      <option value="Dillard University">Dillard University</option>
                      <option value="Doane College">Doane College</option>
                      <option value="Dominican University">Dominican University</option>
                      <option value="Dordt College">Dordt College</option>
                      <option value="Drake University">Drake University</option>
                      <option value="Drew University">Drew University</option>
                      <option value="Drexel University">Drexel University</option>
                      <option value="Drury College">Drury College</option>
                      <option value="Duke University">Duke University</option>
                      <option value="Duquesne University">Duquesne University</option>
                      <option value="Earlham College">Earlham College</option>
                      <option value="East Carolina University">East Carolina University</option>
                      <option value="East Central University">East Central University</option>
                      <option value="Eastern Connecticut State University">Eastern Connecticut State University</option>
                      <option value="Eastern Illinois University">Eastern Illinois University</option>
                      <option value="Eastern Kentucky University">Eastern Kentucky University</option>
                      <option value="Eastern Mennonite University">Eastern Mennonite University</option>
                      <option value="Eastern Michigan University">Eastern Michigan University</option>
                      <option value="Eastern Nazarene College">Eastern Nazarene College</option>
                      <option value="Eastern New Mexico University">Eastern New Mexico University</option>
                      <option value="Eastern Washington University">Eastern Washington University</option>
                      <option value="East Stroudsburg State University">East Stroudsburg State University</option>
                      <option value="East Tennessee State University">East Tennessee State University</option>
                      <option value="East Texas Baptist University">East Texas Baptist University</option>
                      <option value="Eckerd College">Eckerd College</option>
                      <option value="Edgewood College">Edgewood College</option>
                      <option value="Edinboro University of Pennsylvania">Edinboro University of Pennsylvania</option>
                      <option value="Elizabeth City State University">Elizabeth City State University</option>
                      <option value="Elizabethtown College">Elizabethtown College</option>
                      <option value="Elmhurst College">Elmhurst College</option>
                      <option value="Elmira College">Elmira College</option>
                      <option value="Elon College">Elon College</option>
                      <option value="Embry-Riddle Aeronautical University">Embry-Riddle Aeronautical University</option>
                      <option value="Emerson College">Emerson College</option>
                      <option value="Emmanuel College">Emmanuel College</option>
                      <option value="Emory & Henry College">Emory & Henry College</option>
                      <option value="Emory University">Emory University</option>
                      <option value="Emporia State University">Emporia State University</option>
                      <option value="Erskine College">Erskine College</option>
                      <option value="Eureka College">Eureka College</option>
                      <option value="Evangel University">Evangel University</option>
                      <option value="Evergreen State College">Evergreen State College</option>
                      <option value="Fairfield University">Fairfield University</option>
                      <option value="Fairleigh Dickinson University, Florham-Madison Campus">Fairleigh Dickinson University, Florham-Madison Campus</option>
                      <option value="Fairmont State College">Fairmont State College</option>
                      <option value="Fayetteville State University">Fayetteville State University</option>
                      <option value="Ferris State University">Ferris State University</option>
                      <option value="Ferrum College">Ferrum College</option>
                      <option value="Fisk University">Fisk University</option>
                      <option value="Fitchburg State College">Fitchburg State College</option>
                      <option value="Flagler College">Flagler College</option>
                      <option value="Florida Agricultural and Mechanical University">Florida Agricultural and Mechanical University</option>
                      <option value="Florida Atlantic University">Florida Atlantic University</option>
                      <option value="Florida Gulf Coast University">Florida Gulf Coast University</option>
                      <option value="Florida Institute of Technology">Florida Institute of Technology</option>
                      <option value="Florida International University">Florida International University</option>
                      <option value="Florida Memorial College">Florida Memorial College</option>
                      <option value="Florida State University">Florida State University</option>
                      <option value="Fordham University">Fordham University</option>
                      <option value="Fort Hays State University">Fort Hays State University</option>
                      <option value="Fort Valley State University">Fort Valley State University</option>
                      <option value="Framingham State College">Framingham State College</option>
                      <option value="Francis Marion University">Francis Marion University</option>
                      <option value="Franklin and Marshall College">Franklin and Marshall College</option>
                      <option value="Franklin College">Franklin College</option>
                      <option value="Franklin Pierce College">Franklin Pierce College</option>
                      <option value="Franklin University">Franklin University</option>
                      <option value="Freed-Hardeman University">Freed-Hardeman University</option>
                      <option value="Fresno Pacific University">Fresno Pacific University</option>
                      <option value="Friends University">Friends University</option>
                      <option value="Frostburg State University">Frostburg State University</option>
                      <option value="Furman University">Furman University</option>
                      <option value="Gallaudet University">Gallaudet University</option>
                      <option value="Gannon University">Gannon University</option>
                      <option value="Gardner-Webb University">Gardner-Webb University</option>
                      <option value="Geneva College">Geneva College</option>
                      <option value="George Fox University">George Fox University</option>
                      <option value="George Mason University">George Mason University</option>
                      <option value="Georgetown College">Georgetown College</option>
                      <option value="Georgetown University">Georgetown University</option>
                      <option value="George Washington University">George Washington University</option>
                      <option value="Georgia College & State University">Georgia College & State University</option>
                      <option value="Georgia Institute of Technology">Georgia Institute of Technology</option>
                      <option value="Georgia Southern University">Georgia Southern University</option>
                      <option value="Georgia Southwestern State University">Georgia Southwestern State University</option>
                      <option value="Georgia State University">Georgia State University</option>
                      <option value="Gettysburg College">Gettysburg College</option>
                      <option value="Gonzaga University">Gonzaga University</option>
                      <option value="Gordon College">Gordon College</option>
                      <option value="Goshen College">Goshen College</option>
                      <option value="Goucher College">Goucher College</option>
                      <option value="Governors State University">Governors State University</option>
                      <option value="Grace College and Theological Seminary">Grace College and Theological Seminary</option>
                      <option value="Graceland College">Graceland College</option>
                      <option value="Grambling State University">Grambling State University</option>
                      <option value="Grand Canyon University">Grand Canyon University</option>
                      <option value="Grand Valley State University">Grand Valley State University</option>
                      <option value="Grand View College">Grand View College</option>
                      <option value="Grinnell College">Grinnell College</option>
                      <option value="Grove City College">Grove City College</option>
                      <option value="Guilford College">Guilford College</option>
                      <option value="Gustavus Adolphus College">Gustavus Adolphus College</option>
                      <option value="Hamilton College">Hamilton College</option>
                      <option value="Hamline University">Hamline University</option>
                      <option value="Hampden-Sydney College">Hampden-Sydney College</option>
                      <option value="Hampton University">Hampton University</option>
                      <option value="Hanover College">Hanover College</option>
                      <option value="Harding University">Harding University</option>
                      <option value="Hardin-Simmons University">Hardin-Simmons University</option>
                      <option value="Harris-Stowe State College">Harris-Stowe State College</option>
                      <option value="Hartwick College">Hartwick College</option>
                      <option value="Harvard University">Harvard University</option>
                      <option value="Harvey Mudd College">Harvey Mudd College</option>
                      <option value="Haverford College">Haverford College</option>
                      <option value="Hawaii Pacific University">Hawaii Pacific University</option>
                      <option value="Hendrix College">Hendrix College</option>
                      <option value="High Point University">High Point University</option>
                      <option value="Hillsdale College">Hillsdale College</option>
                      <option value="Hiram College">Hiram College</option>
                      <option value="Hobart and William Smith Colleges">Hobart and William Smith Colleges</option>
                      <option value="Hofstra University">Hofstra University</option>
                      <option value="Hollins University">Hollins University</option>
                      <option value="Holy Names College">Holy Names College</option>
                      <option value="Hood College">Hood College</option>
                      <option value="Hope College">Hope College</option>
                      <option value="Houghton College">Houghton College</option>
                      <option value="Houston Baptist University">Houston Baptist University</option>
                      <option value="Howard Payne University">Howard Payne University</option>
                      <option value="Howard University">Howard University</option>
                      <option value="Humboldt State University">Humboldt State University</option>
                      <option value="Hunter College">Hunter College</option>
                      <option value="Huntingdon College">Huntingdon College</option>
                      <option value="Huntington College">Huntington College</option>
                      <option value="Husson College">Husson College</option>
                      <option value="Huston-Tillotson College">Huston-Tillotson College</option>
                      <option value="Idaho State University">Idaho State University</option>
                      <option value="Illinois College">Illinois College</option>
                      <option value="Illinois Institute of Technology">Illinois Institute of Technology</option>
                      <option value="Illinois State University">Illinois State University</option>
                      <option value="Illinois Wesleyan University">Illinois Wesleyan University</option>
                      <option value="Immaculata College">Immaculata College</option>
                      <option value="Indiana Institute of Technology">Indiana Institute of Technology</option>
                      <option value="Indiana State University">Indiana State University</option>
                      <option value="Indiana University Bloomington">Indiana University Bloomington</option>
                      <option value="Indiana University Northwest">Indiana University Northwest</option>
                      <option value="Indiana University of Pennsylvania">Indiana University of Pennsylvania</option>
                      <option value="Indiana University-Purdue University Fort Wayne">Indiana University-Purdue University Fort Wayne</option>
                      <option value="Indiana University-Purdue University Indianapolis">Indiana University-Purdue University Indianapolis</option>
                      <option value="Indiana University South Bend">Indiana University South Bend</option>
                      <option value="Indiana University Southeast">Indiana University Southeast</option>
                      <option value="Indiana Wesleyan University">Indiana Wesleyan University</option>
                      <option value="Iona College">Iona College</option>
                      <option value="Iowa State University">Iowa State University</option>
                      <option value="Iowa Wesleyan College">Iowa Wesleyan College</option>
                      <option value="Ithaca College">Ithaca College</option>
                      <option value="Jackson State University">Jackson State University</option>
                      <option value="Jacksonville State University">Jacksonville State University</option>
                      <option value="Jacksonville University">Jacksonville University</option>
                      <option value="James Madison University">James Madison University</option>
                      <option value="Jamestown College">Jamestown College</option>
                      <option value="Jarvis Christian College">Jarvis Christian College</option>
                      <option value="John Brown University">John Brown University</option>
                      <option value="John Carroll University">John Carroll University</option>
                      <option value="Johns Hopkins University">Johns Hopkins University</option>
                      <option value="Johnson C. Smith University">Johnson C. Smith University</option>
                      <option value="Johnson State College">Johnson State College</option>
                      <option value="Judson College">Judson College</option>
                      <option value="Juniata College">Juniata College</option>
                      <option value="Kalamazoo College">Kalamazoo College</option>
                      <option value="Kansas State University">Kansas State University</option>
                      <option value="Kansas Wesleyan University">Kansas Wesleyan University</option>
                      <option value="Kean University">Kean University</option>
                      <option value="Keene State College">Keene State College</option>
                      <option value="Kennesaw State University">Kennesaw State University</option>
                      <option value="Kent State University">Kent State University</option>
                      <option value="Kentucky State University">Kentucky State University</option>
                      <option value="Kentucky Wesleyan College">Kentucky Wesleyan College</option>
                      <option value="Kenyon College">Kenyon College</option>
                      <option value="Kettering University">Kettering University</option>
                      <option value="Keuka College">Keuka College</option>
                      <option value="King College">King College</option>
                      <option value="Knox College">Knox College</option>
                      <option value="Kutztown University of Pennsylvania">Kutztown University of Pennsylvania</option>
                      <option value="Lafayette College">Lafayette College</option>
                      <option value="LaGrange College">LaGrange College</option>
                      <option value="Lake Erie College">Lake Erie College</option>
                      <option value="Lake Forest College">Lake Forest College</option>
                      <option value="Lakeland College">Lakeland College</option>
                      <option value="Lake Superior State University">Lake Superior State University</option>
                      <option value="Lamar University">Lamar University</option>
                      <option value="Lambuth University">Lambuth University</option>
                      <option value="Lancaster Bible College">Lancaster Bible College</option>
                      <option value="Lander University">Lander University</option>
                      <option value="Lane College">Lane College</option>
                      <option value="La Roche College">La Roche College</option>
                      <option value="La Salle University">La Salle University</option>
                      <option value="La Sierra University">La Sierra University</option>
                      <option value="Lawrence Technological University">Lawrence Technological University</option>
                      <option value="Lawrence University">Lawrence University</option>
                      <option value="Lebanon Valley College">Lebanon Valley College</option>
                      <option value="Lee University">Lee University</option>
                      <option value="Lehigh University">Lehigh University</option>
                      <option value="Le Moyne College">Le Moyne College</option>
                      <option value="Le Moyne-Owen College">Le Moyne-Owen College</option>
                      <option value="Lenoir-Rhyne College">Lenoir-Rhyne College</option>
                      <option value="Lesley College">Lesley College</option>
                      <option value="LeTourneau University">LeTourneau University</option>
                      <option value="Lewis and Clark College">Lewis and Clark College</option>
                      <option value="Lewis-Clark State College">Lewis-Clark State College</option>
                      <option value="Lewis University">Lewis University</option>
                      <option value="Liberty University">Liberty University</option>
                      <option value="LIM College">LIM College</option>
                      <option value="Limestone College">Limestone College</option>
                      <option value="Lincoln Memorial University">Lincoln Memorial University</option>
                      <option value="Lincoln University Missouri">Lincoln University Missouri</option>
                      <option value="Lincoln University Pennsylvania">Lincoln University Pennsylvania</option>
                      <option value="Lindenwood University">Lindenwood University</option>
                      <option value="Lindsey Wilson College">Lindsey Wilson College</option>
                      <option value="Linfield College">Linfield College</option>
                      <option value="Livingstone College">Livingstone College</option>
                      <option value="Lock Haven University of Pennsylvania">Lock Haven University of Pennsylvania</option>
                      <option value="Long Island University, Brooklyn Campus">Long Island University, Brooklyn Campus</option>
                      <option value="Long Island University, C.W. Post Campus">Long Island University, C.W. Post Campus</option>
                      <option value="Longwood University">Longwood University</option>
                      <option value="Loras College">Loras College</option>
                      <option value="Louisiana College">Louisiana College</option>
                      <option value="Louisiana State University and Agricultural and Mechanical College">Louisiana State University and Agricultural and Mechanical College</option>
                      <option value="Louisiana State University in Shreveport">Louisiana State University in Shreveport</option>
                      <option value="Louisiana Tech University">Louisiana Tech University</option>
                      <option value="Lourdes College">Lourdes College</option>
                      <option value="Loyola College in Maryland">Loyola College in Maryland</option>
                      <option value="Loyola Marymount University">Loyola Marymount University</option>
                      <option value="Loyola University Chicago">Loyola University Chicago</option>
                      <option value="Loyola University New Orleans">Loyola University New Orleans</option>
                      <option value="Lubbock Christian University">Lubbock Christian University</option>
                      <option value="Luther College">Luther College</option>
                      <option value="Lycoming College">Lycoming College</option>
                      <option value="Lynchburg College">Lynchburg College</option>
                      <option value="Lyndon State College">Lyndon State College</option>
                      <option value="Lynn University">Lynn University</option>
                      <option value="Lyon College">Lyon College</option>
                      <option value="Macalester College">Macalester College</option>
                      <option value="MacMurray College">MacMurray College</option>
                      <option value="Madonna University">Madonna University</option>
                      <option value="Maharishi University of Management">Maharishi University of Management</option>
                      <option value="Maine Maritime Academy">Maine Maritime Academy</option>
                      <option value="Malone College">Malone College</option>
                      <option value="Manchester College">Manchester College</option>
                      <option value="Manhattan College">Manhattan College</option>
                      <option value="Manhattanville College">Manhattanville College</option>
                      <option value="Mankato State University">Mankato State University</option>
                      <option value="Mansfield University of Pennsylvania">Mansfield University of Pennsylvania</option>
                      <option value="Maranatha Baptist Bible College">Maranatha Baptist Bible College</option>
                      <option value="Marian College">Marian College</option>
                      <option value="Marietta College">Marietta College</option>
                      <option value="Marist College">Marist College</option>
                      <option value="Marlboro College">Marlboro College</option>
                      <option value="Marquette University">Marquette University</option>
                      <option value="Mars Hill College">Mars Hill College</option>
                      <option value="Marshall University">Marshall University</option>
                      <option value="Mary Baldwin College">Mary Baldwin College</option>
                      <option value="Marycrest International University">Marycrest International University</option>
                      <option value="Marygrove College">Marygrove College</option>
                      <option value="Maryland Institute, College of Art">Maryland Institute, College of Art</option>
                      <option value="Marymount College">Marymount College</option>
                      <option value="Marymount Manhattan College">Marymount Manhattan College</option>
                      <option value="Marymount University">Marymount University</option>
                      <option value="Maryville College">Maryville College</option>
                      <option value="Maryville University of Saint Louis">Maryville University of Saint Louis</option>
                      <option value="Marywood University">Marywood University</option>
                      <option value="Massachusetts College of Liberal Arts">Massachusetts College of Liberal Arts</option>
                      <option value="Massachusetts Institute of Technology">Massachusetts Institute of Technology</option>
                      <option value="Massachusetts Maritime Academy">Massachusetts Maritime Academy</option>
                      <option value="Master's College and Seminary">Master's College and Seminary</option>
                      <option value="Mayville State University">Mayville State University</option>
                      <option value="McDaniel College">McDaniel College</option>
                      <option value="McKendree College">McKendree College</option>
                      <option value="McMurry University">McMurry University</option>
                      <option value="McNeese State University">McNeese State University</option>
                      <option value="McPherson College">McPherson College</option>
                      <option value="Medaille College">Medaille College</option>
                      <option value="Medgar Evers College">Medgar Evers College</option>
                      <option value="Memphis College of Art">Memphis College of Art</option>
                      <option value="Menlo College">Menlo College</option>
                      <option value="Mercer University">Mercer University</option>
                      <option value="Mercy College">Mercy College</option>
                      <option value="Mercyhurst College">Mercyhurst College</option>
                      <option value="Meredith College">Meredith College</option>
                      <option value="Merrimack College">Merrimack College</option>
                      <option value="Mesa State College">Mesa State College</option>
                      <option value="Messiah College">Messiah College</option>
                      <option value="Methodist College">Methodist College</option>
                      <option value="Metropolitan State College of Denver">Metropolitan State College of Denver</option>
                      <option value="Metropolitan State University">Metropolitan State University</option>
                      <option value="Miami University">Miami University</option>
                      <option value="Michigan State University">Michigan State University</option>
                      <option value="Michigan Technological University">Michigan Technological University</option>
                      <option value="MidAmerica Nazarene University">MidAmerica Nazarene University</option>
                      <option value="Middle Tennessee State University">Middle Tennessee State University</option>
                      <option value="Middlebury College">Middlebury College</option>
                      <option value="Midland Lutheran College">Midland Lutheran College</option>
                      <option value="Midwestern State University">Midwestern State University</option>
                      <option value="Miles College">Miles College</option>
                      <option value="Millersville University of Pennsylvania">Millersville University of Pennsylvania</option>
                      <option value="Milligan College">Milligan College</option>
                      <option value="Millikin University">Millikin University</option>
                      <option value="Mills College">Mills College</option>
                      <option value="Millsaps College">Millsaps College</option>
                      <option value="Milwaukee School of Engineering">Milwaukee School of Engineering</option>
                      <option value="Minneapolis College of Art and Design">Minneapolis College of Art and Design</option>
                      <option value="Minot State University">Minot State University</option>
                      <option value="Mississippi College">Mississippi College</option>
                      <option value="Mississippi State University">Mississippi State University</option>
                      <option value="Mississippi University for Women">Mississippi University for Women</option>
                      <option value="Mississippi Valley State University">Mississippi Valley State University</option>
                      <option value="Missouri Baptist University">Missouri Baptist University</option>
                      <option value="Missouri Southern State University">Missouri Southern State University</option>
                      <option value="Missouri State University">Missouri State University</option>
                      <option value="Missouri University of Science and Technology">Missouri University of Science and Technology</option>
                      <option value="Missouri Valley College">Missouri Valley College</option>
                      <option value="Missouri Western State College">Missouri Western State College</option>
                      <option value="Molloy College">Molloy College</option>
                      <option value="Monmouth College">Monmouth College</option>
                      <option value="Monmouth University">Monmouth University</option>
                      <option value="Montana State University - Billings">Montana State University - Billings</option>
                      <option value="Montana State University - Bozeman">Montana State University - Bozeman</option>
                      <option value="Montana State University - Northern">Montana State University - Northern</option>
                      <option value="Montana Tech of the University of Montana">Montana Tech of the University of Montana</option>
                      <option value="Montclair State University">Montclair State University</option>
                      <option value="Montreat College">Montreat College</option>
                      <option value="Montserrat College of Art">Montserrat College of Art</option>
                      <option value="Moody Bible Institute">Moody Bible Institute</option>
                      <option value="Moravian College">Moravian College</option>
                      <option value="Morehead State University">Morehead State University</option>
                      <option value="Morehouse College">Morehouse College</option>
                      <option value="Morgan State University">Morgan State University</option>
                      <option value="Morningside College">Morningside College</option>
                      <option value="Morris Brown College">Morris Brown College</option>
                      <option value="Morris College">Morris College</option>
                      <option value="Mount Holyoke College">Mount Holyoke College</option>
                      <option value="Mount Marty College">Mount Marty College</option>
                      <option value="Mount Mary College">Mount Mary College</option>
                      <option value="Mount Mercy College">Mount Mercy College</option>
                      <option value="Mount Olive College">Mount Olive College</option>
                      <option value="Mount Saint Mary College">Mount Saint Mary College</option>
                      <option value="Mount Saint Mary's College">Mount Saint Mary's College</option>
                      <option value="Mount St. Mary's College and Seminary">Mount St. Mary's College and Seminary</option>
                      <option value="Mount Union College">Mount Union College</option>
                      <option value="Mount Vernon Nazarene University">Mount Vernon Nazarene University</option>
                      <option value="Muhlenberg College">Muhlenberg College</option>
                      <option value="Multnomah Bible College and Biblical Seminary">Multnomah Bible College and Biblical Seminary</option>
                      <option value="Murray State University">Murray State University</option>
                      <option value="Muskingum College">Muskingum College</option>
                      <option value="Naropa University">Naropa University</option>
                      <option value="National-Louis University">National-Louis University</option>
                      <option value="National University">National University</option>
                      <option value="Nazareth College of Rochester">Nazareth College of Rochester</option>
                      <option value="Nebraska Wesleyan University">Nebraska Wesleyan University</option>
                      <option value="Neumann College">Neumann College</option>
                      <option value="Newberry College">Newberry College</option>
                      <option value="New College of Florida">New College of Florida</option>
                      <option value="New England College">New England College</option>
                      <option value="New England Conservatory of Music">New England Conservatory of Music</option>
                      <option value="New Jersey City University">New Jersey City University</option>
                      <option value="New Jersey Institute of Technology">New Jersey Institute of Technology</option>
                      <option value="Newman University">Newman University</option>
                      <option value="New Mexico Highlands University">New Mexico Highlands University</option>
                      <option value="New Mexico Institute of Mining and Technology">New Mexico Institute of Mining and Technology</option>
                      <option value="New Mexico State University">New Mexico State University</option>
                      <option value="New York Institute of Technology">New York Institute of Technology</option>
                      <option value="New York University">New York University</option>
                      <option value="Niagara University">Niagara University</option>
                      <option value="Nicholls State University">Nicholls State University</option>
                      <option value="Norfolk State University">Norfolk State University</option>
                      <option value="North Carolina Agricultural and Technical State University">North Carolina Agricultural and Technical State University</option>
                      <option value="North Carolina Central University">North Carolina Central University</option>
                      <option value="North Carolina State University at Raleigh">North Carolina State University at Raleigh</option>
                      <option value="North Carolina Wesleyan College">North Carolina Wesleyan College</option>
                      <option value="North Central College">North Central College</option>
                      <option value="North Dakota State University">North Dakota State University</option>
                      <option value="Northeastern Illinois University">Northeastern Illinois University</option>
                      <option value="Northeastern State University">Northeastern State University</option>
                      <option value="Northeastern University">Northeastern University</option>
                      <option value="Northern Arizona University">Northern Arizona University</option>
                      <option value="Northern Illinois University">Northern Illinois University</option>
                      <option value="Northern Kentucky University">Northern Kentucky University</option>
                      <option value="Northern Michigan University">Northern Michigan University</option>
                      <option value="Northern State University">Northern State University</option>
                      <option value="North Georgia College & State University">North Georgia College & State University</option>
                      <option value="North Greenville College">North Greenville College</option>
                      <option value="North Park University">North Park University</option>
                      <option value="Northrop University">Northrop University</option>
                      <option value="Northwest Christian College">Northwest Christian College</option>
                      <option value="Northwestern College Iowa">Northwestern College Iowa</option>
                      <option value="Northwestern College of Chiropractic">Northwestern College of Chiropractic</option>
                      <option value="Northwestern Oklahoma State University">Northwestern Oklahoma State University</option>
                      <option value="Northwestern State University of Louisiana">Northwestern State University of Louisiana</option>
                      <option value="Northwestern University">Northwestern University</option>
                      <option value="Northwest Missouri State University">Northwest Missouri State University</option>
                      <option value="Northwest Nazarene University">Northwest Nazarene University</option>
                      <option value="Northwest University">Northwest University</option>
                      <option value="Northwood University">Northwood University</option>
                      <option value="Norwich University">Norwich University</option>
                      <option value="Notre Dame College">Notre Dame College</option>
                      <option value="Notre Dame de Namur University">Notre Dame de Namur University</option>
                      <option value="Nova Southeastern University">Nova Southeastern University</option>
                      <option value="Nyack College">Nyack College</option>
                      <option value="Oakland City University">Oakland City University</option>
                      <option value="Oakland University">Oakland University</option>
                      <option value="Oakwood College">Oakwood College</option>
                      <option value="Oberlin College">Oberlin College</option>
                      <option value="Occidental College">Occidental College</option>
                      <option value="Oglala Lakota College">Oglala Lakota College</option>
                      <option value="Oglethorpe University">Oglethorpe University</option>
                      <option value="Ohio Dominican University">Ohio Dominican University</option>
                      <option value="Ohio Northern University">Ohio Northern University</option>
                      <option value="Ohio State University">Ohio State University</option>
                      <option value="Ohio State University, Lima Campus">Ohio State University, Lima Campus</option>
                      <option value="Ohio State University, Mansfield Campus">Ohio State University, Mansfield Campus</option>
                      <option value="Ohio State University, Marion Campus">Ohio State University, Marion Campus</option>
                      <option value="Ohio State University, Newark Campus">Ohio State University, Newark Campus</option>
                      <option value="Ohio University">Ohio University</option>
                      <option value="Ohio Valley College">Ohio Valley College</option>
                      <option value="Ohio Wesleyan University">Ohio Wesleyan University</option>
                      <option value="Oklahoma Baptist University">Oklahoma Baptist University</option>
                      <option value="Oklahoma Christian University of Science and Arts">Oklahoma Christian University of Science and Arts</option>
                      <option value="Oklahoma City University">Oklahoma City University</option>
                      <option value="Oklahoma Panhandle State University">Oklahoma Panhandle State University</option>
                      <option value="Oklahoma State University">Oklahoma State University</option>
                      <option value="Old Dominion University">Old Dominion University</option>
                      <option value="Olivet College">Olivet College</option>
                      <option value="Olivet Nazarene University">Olivet Nazarene University</option>
                      <option value="Oral Roberts University">Oral Roberts University</option>
                      <option value="Oregon Health Sciences University">Oregon Health Sciences University</option>
                      <option value="Oregon Institute of Technology">Oregon Institute of Technology</option>
                      <option value="Oregon State University">Oregon State University</option>
                      <option value="Otis College of Art and Design">Otis College of Art and Design</option>
                      <option value="Otterbein College">Otterbein College</option>
                      <option value="Ouachita Baptist University">Ouachita Baptist University</option>
                      <option value="Our Lady of Holy Cross College">Our Lady of Holy Cross College</option>
                      <option value="Our Lady of the Lake University of San Antonio">Our Lady of the Lake University of San Antonio</option>
                      <option value="Pace University">Pace University</option>
                      <option value="Pacific Lutheran University">Pacific Lutheran University</option>
                      <option value="Pacific Northwest College of Art">Pacific Northwest College of Art</option>
                      <option value="Pacific Union College">Pacific Union College</option>
                      <option value="Pacific University">Pacific University</option>
                      <option value="Paine College">Paine College</option>
                      <option value="Palm Beach Atlantic University">Palm Beach Atlantic University</option>
                      <option value="Park University">Park University</option>
                      <option value="Paul Quinn College">Paul Quinn College</option>
                      <option value="Peace College">Peace College</option>
                      <option value="Pennsylvania College of Technology">Pennsylvania College of Technology</option>
                      <option value="Pennsylvania State University">Pennsylvania State University</option>
                      <option value="Pepperdine University">Pepperdine University</option>
                      <option value="Peru State College">Peru State College</option>
                      <option value="Pfeiffer University">Pfeiffer University</option>
                      <option value="Philadelphia University">Philadelphia University</option>
                      <option value="Philander Smith College">Philander Smith College</option>
                      <option value="Piedmont College">Piedmont College</option>
                      <option value="Pikeville College">Pikeville College</option>
                      <option value="Pittsburg State University">Pittsburg State University</option>
                      <option value="Pitzer College">Pitzer College</option>
                      <option value="Plymouth State University">Plymouth State University</option>
                      <option value="Point Loma Nazarene University">Point Loma Nazarene University</option>
                      <option value="Point Park College">Point Park College</option>
                      <option value="Polytechnic University, Brooklyn Campus">Polytechnic University, Brooklyn Campus</option>
                      <option value="Pomona College">Pomona College</option>
                      <option value="Portland State University">Portland State University</option>
                      <option value="Prairie View A&M University">Prairie View A&M University</option>
                      <option value="Pratt Institute">Pratt Institute</option>
                      <option value="Presbyterian College">Presbyterian College</option>
                      <option value="Prescott College">Prescott College</option>
                      <option value="Princeton University">Princeton University</option>
                      <option value="Principia College">Principia College</option>
                      <option value="Providence College">Providence College</option>
                      <option value="Purdue University">Purdue University</option>
                      <option value="Purdue University Calumet">Purdue University Calumet</option>
                      <option value="Purdue University North Central">Purdue University North Central</option>
                      <option value="Queens College">Queens College</option>
                      <option value="Queens University of Charlotte">Queens University of Charlotte</option>
                      <option value="Quincy University">Quincy University</option>
                      <option value="Quinnipiac University">Quinnipiac University</option>
                      <option value="Radford University">Radford University</option>
                      <option value="Ramapo College of New Jersey">Ramapo College of New Jersey</option>
                      <option value="Randolph College">Randolph College</option>
                      <option value="Randolph-Macon College">Randolph-Macon College</option>
                      <option value="Reed College">Reed College</option>
                      <option value="Regis College">Regis College</option>
                      <option value="Regis University">Regis University</option>
                      <option value="Rensselaer Polytechnic Institute">Rensselaer Polytechnic Institute</option>
                      <option value="Rhode Island College">Rhode Island College</option>
                      <option value="Rhode Island School of Design">Rhode Island School of Design</option>
                      <option value="Rhodes College">Rhodes College</option>
                      <option value="Rice University">Rice University</option>
                      <option value="Richard Stockton College of New Jersey">Richard Stockton College of New Jersey</option>
                      <option value="Rider University">Rider University</option>
                      <option value="Ringling School of Art and Design">Ringling School of Art and Design</option>
                      <option value="Ripon College">Ripon College</option>
                      <option value="Rivier College">Rivier College</option>
                      <option value="Roanoke College">Roanoke College</option>
                      <option value="Robert Morris College">Robert Morris College</option>
                      <option value="Roberts Wesleyan College">Roberts Wesleyan College</option>
                      <option value="Rochester College">Rochester College</option>
                      <option value="Rochester Institute of Technology">Rochester Institute of Technology</option>
                      <option value="Rockford College">Rockford College</option>
                      <option value="Rockhurst University">Rockhurst University</option>
                      <option value="Rocky Mountain College">Rocky Mountain College</option>
                      <option value="Roger Williams University">Roger Williams University</option>
                      <option value="Rollins College">Rollins College</option>
                      <option value="Roosevelt University">Roosevelt University</option>
                      <option value="Rose-Hulman Institute of Technology">Rose-Hulman Institute of Technology</option>
                      <option value="Rosemont College">Rosemont College</option>
                      <option value="Rowan University">Rowan University</option>
                      <option value="Russell Sage College">Russell Sage College</option>
                      <option value="Rust College">Rust College</option>
                      <option value="Rutgers, The State University of New Jersey, Camden">Rutgers, The State University of New Jersey, Camden</option>
                      <option value="Rutgers, The State University of New Jersey, New Brunswick">Rutgers, The State University of New Jersey, New Brunswick</option>
                      <option value="Rutgers, The State University of New Jersey, Newark">Rutgers, The State University of New Jersey, Newark</option>
                      <option value="Sacred Heart University">Sacred Heart University</option>
                      <option value="Saginaw Valley State University">Saginaw Valley State University</option>
                      <option value="Saint Ambrose University">Saint Ambrose University</option>
                      <option value="Saint Anselm College">Saint Anselm College</option>
                      <option value="Saint Augustine's College">Saint Augustine's College</option>
                      <option value="Saint Bonaventure University">Saint Bonaventure University</option>
                      <option value="Saint Cloud State University">Saint Cloud State University</option>
                      <option value="Saint Edward's University">Saint Edward's University</option>
                      <option value="Saint Francis College">Saint Francis College</option>
                      <option value="Saint Francis University">Saint Francis University</option>
                      <option value="Saint John Fisher College">Saint John Fisher College</option>
                      <option value="Saint John's University">Saint John's University</option>
                      <option value="Saint Joseph College">Saint Joseph College</option>
                      <option value="Saint Joseph's College">Saint Joseph's College</option>
                      <option value="Saint Joseph's College, Maine">Saint Joseph's College, Maine</option>
                      <option value="Saint Joseph's College of Maine">Saint Joseph's College of Maine</option>
                      <option value="Saint Joseph's University">Saint Joseph's University</option>
                      <option value="Saint Lawrence University">Saint Lawrence University</option>
                      <option value="Saint Leo University">Saint Leo University</option>
                      <option value="Saint Louis University">Saint Louis University</option>
                      <option value="Saint Martin's University">Saint Martin's University</option>
                      <option value="Saint Mary-of-the-Woods College">Saint Mary-of-the-Woods College</option>
                      <option value="Saint Mary's College">Saint Mary's College</option>
                      <option value="Saint Mary's College of California">Saint Mary's College of California</option>
                      <option value="Saint Mary's University of Minnesota">Saint Mary's University of Minnesota</option>
                      <option value="Saint Mary's University of San Antonio">Saint Mary's University of San Antonio</option>
                      <option value="Saint Michael's College">Saint Michael's College</option>
                      <option value="Saint Norbert College">Saint Norbert College</option>
                      <option value="Saint Olaf College">Saint Olaf College</option>
                      <option value="Saint Paul's College">Saint Paul's College</option>
                      <option value="Saint Peter's College">Saint Peter's College</option>
                      <option value="Saint Thomas Aquinas College">Saint Thomas Aquinas College</option>
                      <option value="Saint Thomas University">Saint Thomas University</option>
                      <option value="Saint Vincent College">Saint Vincent College</option>
                      <option value="Saint Xavier University">Saint Xavier University</option>
                      <option value="Salem College">Salem College</option>
                      <option value="Salem International University">Salem International University</option>
                      <option value="Salem State College">Salem State College</option>
                      <option value="Salisbury University">Salisbury University</option>
                      <option value="Salve Regina University">Salve Regina University</option>
                      <option value="Samford University">Samford University</option>
                      <option value="Sam Houston State University">Sam Houston State University</option>
                      <option value="San Diego State University">San Diego State University</option>
                      <option value="San Francisco State University">San Francisco State University</option>
                      <option value="San Jose State University">San Jose State University</option>
                      <option value="Santa Clara University">Santa Clara University</option>
                      <option value="Sarah Lawrence College">Sarah Lawrence College</option>
                      <option value="Savannah College of Art and Design">Savannah College of Art and Design</option>
                      <option value="Savannah State University">Savannah State University</option>
                      <option value="School of the Art Institute of Chicago">School of the Art Institute of Chicago</option>
                      <option value="School of the Museum of Fine Arts, Boston">School of the Museum of Fine Arts, Boston</option>
                      <option value="School of Visual Arts">School of Visual Arts</option>
                      <option value="Scripps College">Scripps College</option>
                      <option value="Seattle Pacific University">Seattle Pacific University</option>
                      <option value="Seattle University">Seattle University</option>
                      <option value="Seton Hall University">Seton Hall University</option>
                      <option value="Seton Hill University">Seton Hill University</option>
                      <option value="Shaw University">Shaw University</option>
                      <option value="Shawnee State University">Shawnee State University</option>
                      <option value="Sheldon Jackson College">Sheldon Jackson College</option>
                      <option value="Shenandoah University">Shenandoah University</option>
                      <option value="Shepherd College">Shepherd College</option>
                      <option value="Shippensburg University of Pennsylvania">Shippensburg University of Pennsylvania</option>
                      <option value="Shorter College">Shorter College</option>
                      <option value="Siena College">Siena College</option>
                      <option value="Siena Heights University">Siena Heights University</option>
                      <option value="Sierra Nevada College">Sierra Nevada College</option>
                      <option value="Simmons College">Simmons College</option>
                      <option value="Simpson College">Simpson College</option>
                      <option value="Skidmore College">Skidmore College</option>
                      <option value="Slippery Rock University of Pennsylvania">Slippery Rock University of Pennsylvania</option>
                      <option value="Smith College">Smith College</option>
                      <option value="Sonoma State University">Sonoma State University</option>
                      <option value="South Carolina State University">South Carolina State University</option>
                      <option value="South Dakota School of Mines and Technology">South Dakota School of Mines and Technology</option>
                      <option value="South Dakota State University">South Dakota State University</option>
                      <option value="Southeastern College of the Assemblies of God">Southeastern College of the Assemblies of God</option>
                      <option value="Southeastern Louisiana University">Southeastern Louisiana University</option>
                      <option value="Southeastern Oklahoma State University">Southeastern Oklahoma State University</option>
                      <option value="Southeastern University">Southeastern University</option>
                      <option value="Southeast Missouri State University">Southeast Missouri State University</option>
                      <option value="Southern Adventist University">Southern Adventist University</option>
                      <option value="Southern Arkansas University">Southern Arkansas University</option>
                      <option value="Southern California College">Southern California College</option>
                      <option value="Southern Connecticut State University">Southern Connecticut State University</option>
                      <option value="Southern Illinois University Carbondale">Southern Illinois University Carbondale</option>
                      <option value="Southern Illinois University Edwardsville">Southern Illinois University Edwardsville</option>
                      <option value="Southern Methodist University">Southern Methodist University</option>
                      <option value="Southern Nazarene University">Southern Nazarene University</option>
                      <option value="Southern New Hampshire University">Southern New Hampshire University</option>
                      <option value="Southern Oregon University">Southern Oregon University</option>
                      <option value="Southern Polytechnic State University">Southern Polytechnic State University</option>
                      <option value="Southern University and A&M College">Southern University and A&M College</option>
                      <option value="Southern University at New Orleans">Southern University at New Orleans</option>
                      <option value="Southern Utah University">Southern Utah University</option>
                      <option value="Southern Vermont College">Southern Vermont College</option>
                      <option value="Southern Wesleyan University">Southern Wesleyan University</option>
                      <option value="South Texas College of Law">South Texas College of Law</option>
                      <option value="Southwest Baptist University">Southwest Baptist University</option>
                      <option value="Southwestern Adventist University">Southwestern Adventist University</option>
                      <option value="Southwestern College">Southwestern College</option>
                      <option value="Southwestern Oklahoma State University">Southwestern Oklahoma State University</option>
                      <option value="Southwestern University">Southwestern University</option>
                      <option value="Southwest Minnesota State University">Southwest Minnesota State University</option>
                      <option value="Southwest Missouri State University">Southwest Missouri State University</option>
                      <option value="Southwest Texas State University">Southwest Texas State University</option>
                      <option value="Spalding University">Spalding University</option>
                      <option value="Spelman College">Spelman College</option>
                      <option value="Spring Arbor University">Spring Arbor University</option>
                      <option value="Springfield College">Springfield College</option>
                      <option value="Spring Hill College">Spring Hill College</option>
                      <option value="Stanford University">Stanford University</option>
                      <option value="State University of New York at Albany">State University of New York at Albany</option>
                      <option value="State University of New York at Binghamton">State University of New York at Binghamton</option>
                      <option value="State University of New York at Buffalo">State University of New York at Buffalo</option>
                      <option value="State University of New York at New Paltz">State University of New York at New Paltz</option>
                      <option value="State University of New York at Oswego">State University of New York at Oswego</option>
                      <option value="State University of New York College at Brockport">State University of New York College at Brockport</option>
                      <option value="State University of New York College at Cortland">State University of New York College at Cortland</option>
                      <option value="State University of New York College at Geneseo">State University of New York College at Geneseo</option>
                      <option value="State University of New York College at Oneonta">State University of New York College at Oneonta</option>
                      <option value="State University of New York College at Plattsburgh">State University of New York College at Plattsburgh</option>
                      <option value="State University of New York College at Potsdam">State University of New York College at Potsdam</option>
                      <option value="State University of New York College of Environmental Science and Forestry">State University of New York College of Environmental Science and Forestry</option>
                      <option value="State University of New York Empire State College">State University of New York Empire State College</option>
                      <option value="State University of New York Health Science Center at Brooklyn">State University of New York Health Science Center at Brooklyn</option>
                      <option value="State University of New York Institute of Technology at Utica/Rome">State University of New York Institute of Technology at Utica/Rome</option>
                      <option value="State University of New York Maritime College">State University of New York Maritime College</option>
                      <option value="State University of New York, Stony Brook University">State University of New York, Stony Brook University</option>
                      <option value="State University of West Georgia">State University of West Georgia</option>
                      <option value="Stephen F. Austin State University">Stephen F. Austin State University</option>
                      <option value="Stephens College">Stephens College</option>
                      <option value="Sterling College">Sterling College</option>
                      <option value="Stetson University">Stetson University</option>
                      <option value="Stevens Institute of Technology">Stevens Institute of Technology</option>
                      <option value="Stillman College">Stillman College</option>
                      <option value="Stonehill College">Stonehill College</option>
                      <option value="Suffolk University">Suffolk University</option>
                      <option value="Sul Ross State University">Sul Ross State University</option>
                      <option value="Susquehanna University">Susquehanna University</option>
                      <option value="Swarthmore College">Swarthmore College</option>
                      <option value="Sweet Briar College">Sweet Briar College</option>
                      <option value="Syracuse University">Syracuse University</option>
                      <option value="Tabor College">Tabor College</option>
                      <option value="Talladega College">Talladega College</option>
                      <option value="Tarleton State University">Tarleton State University</option>
                      <option value="Taylor University">Taylor University</option>
                      <option value="Temple University">Temple University</option>
                      <option value="Tennessee State University">Tennessee State University</option>
                      <option value="Tennessee Technological University">Tennessee Technological University</option>
                      <option value="Tennessee Wesleyan College">Tennessee Wesleyan College</option>
                      <option value="Texas A&M International University">Texas A&M International University</option>
                      <option value="Texas A&M University">Texas A&M University</option>
                      <option value="Texas A&M University - Commerce">Texas A&M University - Commerce</option>
                      <option value="Texas A&M University - Corpus Christi">Texas A&M University - Corpus Christi</option>
                      <option value="Texas A&M University - Kingsville">Texas A&M University - Kingsville</option>
                      <option value="Texas Christian University">Texas Christian University</option>
                      <option value="Texas College">Texas College</option>
                      <option value="Texas Lutheran University">Texas Lutheran University</option>
                      <option value="Texas Southern University">Texas Southern University</option>
                      <option value="Texas State University">Texas State University</option>
                      <option value="Texas Tech University">Texas Tech University</option>
                      <option value="Texas Wesleyan University">Texas Wesleyan University</option>
                      <option value="Texas Woman's University">Texas Woman's University</option>
                      <option value="The Citadel">The Citadel</option>
                      <option value="The College of New Jersey">The College of New Jersey</option>
                      <option value="Thiel College">Thiel College</option>
                      <option value="Thomas Aquinas College">Thomas Aquinas College</option>
                      <option value="Thomas College">Thomas College</option>
                      <option value="Thomas More College">Thomas More College</option>
                      <option value="Tiffin University">Tiffin University</option>
                      <option value="Toccoa Falls College">Toccoa Falls College</option>
                      <option value="Tougaloo College">Tougaloo College</option>
                      <option value="Towson University">Towson University</option>
                      <option value="Transylvania University">Transylvania University</option>
                      <option value="Trevecca Nazarene University">Trevecca Nazarene University</option>
                      <option value="Tri-State University">Tri-State University</option>
                      <option value="Trinity Christian College">Trinity Christian College</option>
                      <option value="Trinity College">Trinity College</option>
                      <option value="Trinity International University">Trinity International University</option>
                      <option value="Trinity University">Trinity University</option>
                      <option value="Troy University">Troy University</option>
                      <option value="Truman State University">Truman State University</option>
                      <option value="Tufts University">Tufts University</option>
                      <option value="Tulane University">Tulane University</option>
                      <option value="Tusculum College">Tusculum College</option>
                      <option value="Tuskegee University">Tuskegee University</option>
                      <option value="Union College">Union College</option>
                      <option value="Union University">Union University</option>
                      <option value="United States Air Force Academy">United States Air Force Academy</option>
                      <option value="United States Coast Guard Academy">United States Coast Guard Academy</option>
                      <option value="United States International University">United States International University</option>
                      <option value="United States Merchant Marine Academy">United States Merchant Marine Academy</option>
                      <option value="United States Military Academy">United States Military Academy</option>
                      <option value="United States Naval Academy">United States Naval Academy</option>
                      <option value="Unity College">Unity College</option>
                      <option value="University of Advancing Technology">University of Advancing Technology</option>
                      <option value="University of Akron">University of Akron</option>
                      <option value="University of Alabama">University of Alabama</option>
                      <option value="University of Alabama at Birmingham">University of Alabama at Birmingham</option>
                      <option value="University of Alabama in Huntsville">University of Alabama in Huntsville</option>
                      <option value="University of Alaska Anchorage">University of Alaska Anchorage</option>
                      <option value="University of Alaska Fairbanks">University of Alaska Fairbanks</option>
                      <option value="University of Alaska Southeast">University of Alaska Southeast</option>
                      <option value="University of Arizona">University of Arizona</option>
                      <option value="University of Arkansas">University of Arkansas</option>
                      <option value="University of Arkansas at Little Rock">University of Arkansas at Little Rock</option>
                      <option value="University of Arkansas at Monticello">University of Arkansas at Monticello</option>
                      <option value="University of Arkansas at Pine Bluff">University of Arkansas at Pine Bluff</option>
                      <option value="University of Baltimore">University of Baltimore</option>
                      <option value="University of Bridgeport">University of Bridgeport</option>
                      <option value="University of California, Berkeley">University of California, Berkeley</option>
                      <option value="University of California, Davis">University of California, Davis</option>
                      <option value="University of California, Irvine">University of California, Irvine</option>
                      <option value="University of California, Los Angeles">University of California, Los Angeles</option>
                      <option value="University of California, Merced">University of California, Merced</option>
                      <option value="University of California, Riverside">University of California, Riverside</option>
                      <option value="University of California, San Diego">University of California, San Diego</option>
                      <option value="University of California, Santa Barbara">University of California, Santa Barbara</option>
                      <option value="University of California, Santa Cruz">University of California, Santa Cruz</option>
                      <option value="University of Central Arkansas">University of Central Arkansas</option>
                      <option value="University of Central Florida">University of Central Florida</option>
                      <option value="University of Central Oklahoma">University of Central Oklahoma</option>
                      <option value="University of Charleston">University of Charleston</option>
                      <option value="University of Chicago">University of Chicago</option>
                      <option value="University of Cincinnati">University of Cincinnati</option>
                      <option value="University of Colorado at Boulder">University of Colorado at Boulder</option>
                      <option value="University of Colorado at Colorado Springs">University of Colorado at Colorado Springs</option>
                      <option value="University of Colorado at Denver">University of Colorado at Denver</option>
                      <option value="University of Connecticut">University of Connecticut</option>
                      <option value="University of Dallas">University of Dallas</option>
                      <option value="University of Dayton">University of Dayton</option>
                      <option value="University of Delaware">University of Delaware</option>
                      <option value="University of Denver">University of Denver</option>
                      <option value="University of Detroit Mercy">University of Detroit Mercy</option>
                      <option value="University of Dubuque">University of Dubuque</option>
                      <option value="University of Evansville">University of Evansville</option>
                      <option value="University of Findlay">University of Findlay</option>
                      <option value="University of Florida">University of Florida</option>
                      <option value="University of Georgia">University of Georgia</option>
                      <option value="University of Great Falls">University of Great Falls</option>
                      <option value="University of Hartford">University of Hartford</option>
                      <option value="University of Hawaii at Hilo">University of Hawaii at Hilo</option>
                      <option value="University of Hawaii at Manoa">University of Hawaii at Manoa</option>
                      <option value="University of Houston">University of Houston</option>
                      <option value="University of Houston - Clear Lake">University of Houston - Clear Lake</option>
                      <option value="University of Houston - Downtown">University of Houston - Downtown</option>
                      <option value="University of Houston - Victoria">University of Houston - Victoria</option>
                      <option value="University of Idaho">University of Idaho</option>
                      <option value="University of Illinois at Chicago">University of Illinois at Chicago</option>
                      <option value="University of Illinois at Springfield">University of Illinois at Springfield</option>
                      <option value="University of Illinois at Urbana-Champaign">University of Illinois at Urbana-Champaign</option>
                      <option value="University of Indianapolis">University of Indianapolis</option>
                      <option value="University of Iowa">University of Iowa</option>
                      <option value="University of Kansas">University of Kansas</option>
                      <option value="University of Kentucky">University of Kentucky</option>
                      <option value="University of La Verne">University of La Verne</option>
                      <option value="University of Louisiana at Lafayette">University of Louisiana at Lafayette</option>
                      <option value="University of Louisiana at Monroe">University of Louisiana at Monroe</option>
                      <option value="University of Louisville">University of Louisville</option>
                      <option value="University of Maine">University of Maine</option>
                      <option value="University of Maine at Augusta">University of Maine at Augusta</option>
                      <option value="University of Maine at Farmington">University of Maine at Farmington</option>
                      <option value="University of Maine at Fort Kent">University of Maine at Fort Kent</option>
                      <option value="University of Maine at Machias">University of Maine at Machias</option>
                      <option value="University of Maine at Presque Isle">University of Maine at Presque Isle</option>
                      <option value="University of Mary">University of Mary</option>
                      <option value="University of Maryland, Baltimore County">University of Maryland, Baltimore County</option>
                      <option value="University of Maryland, College Park">University of Maryland, College Park</option>
                      <option value="University of Maryland Eastern Shore">University of Maryland Eastern Shore</option>
                      <option value="University of Maryland University College">University of Maryland University College</option>
                      <option value="University of Mary Hardin-Baylor">University of Mary Hardin-Baylor</option>
                      <option value="University of Mary Washington">University of Mary Washington</option>
                      <option value="University of Massachusetts Amherst">University of Massachusetts Amherst</option>
                      <option value="University of Massachusetts Boston">University of Massachusetts Boston</option>
                      <option value="University of Massachusetts Dartmouth">University of Massachusetts Dartmouth</option>
                      <option value="University of Massachusetts Lowell">University of Massachusetts Lowell</option>
                      <option value="University of Memphis">University of Memphis</option>
                      <option value="University of Miami">University of Miami</option>
                      <option value="University of Michigan">University of Michigan</option>
                      <option value="University of Michigan - Dearborn">University of Michigan - Dearborn</option>
                      <option value="University of Michigan - Flint">University of Michigan - Flint</option>
                      <option value="University of Minnesota, Crookston">University of Minnesota, Crookston</option>
                      <option value="University of Minnesota, Duluth">University of Minnesota, Duluth</option>
                      <option value="University of Minnesota, Morris">University of Minnesota, Morris</option>
                      <option value="University of Minnesota, Twin Cities">University of Minnesota, Twin Cities</option>
                      <option value="University of Mississippi">University of Mississippi</option>
                      <option value="University of Missouri - Columbia">University of Missouri - Columbia</option>
                      <option value="University of Missouri - Kansas City">University of Missouri - Kansas City</option>
                      <option value="University of Missouri - Rolla">University of Missouri - Rolla</option>
                      <option value="University of Missouri - St. Louis">University of Missouri - St. Louis</option>
                      <option value="University of Mobile">University of Mobile</option>
                      <option value="University of Montana - Missoula">University of Montana - Missoula</option>
                      <option value="University of Montevallo">University of Montevallo</option>
                      <option value="University of Nebraska at Kearney">University of Nebraska at Kearney</option>
                      <option value="University of Nebraska at Omaha">University of Nebraska at Omaha</option>
                      <option value="University of Nebraska-Lincoln">University of Nebraska-Lincoln</option>
                      <option value="University of Nevada, Las Vegas">University of Nevada, Las Vegas</option>
                      <option value="University of Nevada, Reno">University of Nevada, Reno</option>
                      <option value="University of New England">University of New England</option>
                      <option value="University of New Hampshire">University of New Hampshire</option>
                      <option value="University of New Haven">University of New Haven</option>
                      <option value="University of New Mexico">University of New Mexico</option>
                      <option value="University of New Orleans">University of New Orleans</option>
                      <option value="University of North Alabama">University of North Alabama</option>
                      <option value="University of North Carolina at Asheville">University of North Carolina at Asheville</option>
                      <option value="University of North Carolina at Chapel Hill">University of North Carolina at Chapel Hill</option>
                      <option value="University of North Carolina at Charlotte">University of North Carolina at Charlotte</option>
                      <option value="University of North Carolina at Greensboro">University of North Carolina at Greensboro</option>
                      <option value="University of North Carolina at Pembroke">University of North Carolina at Pembroke</option>
                      <option value="University of North Carolina at Wilmington">University of North Carolina at Wilmington</option>
                      <option value="University of North Dakota">University of North Dakota</option>
                      <option value="University of Northern Colorado">University of Northern Colorado</option>
                      <option value="University of Northern Iowa">University of Northern Iowa</option>
                      <option value="University of North Florida">University of North Florida</option>
                      <option value="University of North Texas">University of North Texas</option>
                      <option value="University of Notre Dame">University of Notre Dame</option>
                      <option value="University of Oklahoma">University of Oklahoma</option>
                      <option value="University of Oregon">University of Oregon</option>
                      <option value="University of Pennsylvania">University of Pennsylvania</option>
                      <option value="University of Pittsburgh">University of Pittsburgh</option>
                      <option value="University of Pittsburgh at Bradford">University of Pittsburgh at Bradford</option>
                      <option value="University of Pittsburgh at Greensburg">University of Pittsburgh at Greensburg</option>
                      <option value="University of Pittsburgh at Johnstown">University of Pittsburgh at Johnstown</option>
                      <option value="University of Portland">University of Portland</option>
                      <option value="University of Puerto Rico, Mayaguez Campus">University of Puerto Rico, Mayaguez Campus</option>
                      <option value="University of Puerto Rico, Rio Piedras Campus">University of Puerto Rico, Rio Piedras Campus</option>
                      <option value="University of Puget Sound">University of Puget Sound</option>
                      <option value="University of Redlands">University of Redlands</option>
                      <option value="University of Rhode Island">University of Rhode Island</option>
                      <option value="University of Richmond">University of Richmond</option>
                      <option value="University of Rio Grande">University of Rio Grande</option>
                      <option value="University of Rochester">University of Rochester</option>
                      <option value="University of San Diego">University of San Diego</option>
                      <option value="University of San Francisco">University of San Francisco</option>
                      <option value="University of Science and Arts of Oklahoma">University of Science and Arts of Oklahoma</option>
                      <option value="University of Scranton">University of Scranton</option>
                      <option value="University of Sioux Falls">University of Sioux Falls</option>
                      <option value="University of South Alabama">University of South Alabama</option>
                      <option value="University of South Carolina">University of South Carolina</option>
                      <option value="University of South Carolina Aiken">University of South Carolina Aiken</option>
                      <option value="University of South Carolina Upstate">University of South Carolina Upstate</option>
                      <option value="University of South Dakota">University of South Dakota</option>
                      <option value="University of Southern California">University of Southern California</option>
                      <option value="University of Southern Indiana">University of Southern Indiana</option>
                      <option value="University of Southern Maine">University of Southern Maine</option>
                      <option value="University of Southern Mississippi">University of Southern Mississippi</option>
                      <option value="University of South Florida">University of South Florida</option>
                      <option value="University of St. Francis">University of St. Francis</option>
                      <option value="University of St. Thomas">University of St. Thomas</option>
                      <option value="University of Tampa">University of Tampa</option>
                      <option value="University of Tennessee at Chattanooga">University of Tennessee at Chattanooga</option>
                      <option value="University of Tennessee at Knoxville">University of Tennessee at Knoxville</option>
                      <option value="University of Tennessee at Martin">University of Tennessee at Martin</option>
                      <option value="University of Texas at Arlington">University of Texas at Arlington</option>
                      <option value="University of Texas at Austin">University of Texas at Austin</option>
                      <option value="University of Texas at Dallas">University of Texas at Dallas</option>
                      <option value="University of Texas at El Paso">University of Texas at El Paso</option>
                      <option value="University of Texas at San Antonio">University of Texas at San Antonio</option>
                      <option value="University of Texas at Tyler">University of Texas at Tyler</option>
                      <option value="University of Texas - Pan American">University of Texas - Pan American</option>
                      <option value="University of the Arts">University of the Arts</option>
                      <option value="University of the District of Columbia">University of the District of Columbia</option>
                      <option value="University of the Incarnate Word">University of the Incarnate Word</option>
                      <option value="University of the Ozarks">University of the Ozarks</option>
                      <option value="University of the Pacific">University of the Pacific</option>
                      <option value="University of the Sciences in Philadelphia">University of the Sciences in Philadelphia</option>
                      <option value="University of the South">University of the South</option>
                      <option value="University of Toledo">University of Toledo</option>
                      <option value="University of Tulsa">University of Tulsa</option>
                      <option value="University of Utah">University of Utah</option>
                      <option value="University of Vermont">University of Vermont</option>
                      <option value="University of Virginia">University of Virginia</option>
                      <option value="University of Washington">University of Washington</option>
                      <option value="University of West Alabama">University of West Alabama</option>
                      <option value="University of West Florida">University of West Florida</option>
                      <option value="University of West Georgia">University of West Georgia</option>
                      <option value="University of Wisconsin - Eau Claire">University of Wisconsin - Eau Claire</option>
                      <option value="University of Wisconsin - Green Bay">University of Wisconsin - Green Bay</option>
                      <option value="University of Wisconsin - La Crosse">University of Wisconsin - La Crosse</option>
                      <option value="University of Wisconsin - Madison">University of Wisconsin - Madison</option>
                      <option value="University of Wisconsin - Milwaukee">University of Wisconsin - Milwaukee</option>
                      <option value="University of Wisconsin - Oshkosh">University of Wisconsin - Oshkosh</option>
                      <option value="University of Wisconsin - Parkside">University of Wisconsin - Parkside</option>
                      <option value="University of Wisconsin - Platteville">University of Wisconsin - Platteville</option>
                      <option value="University of Wisconsin - River Falls">University of Wisconsin - River Falls</option>
                      <option value="University of Wisconsin - Stevens Point">University of Wisconsin - Stevens Point</option>
                      <option value="University of Wisconsin - Stout">University of Wisconsin - Stout</option>
                      <option value="University of Wisconsin - Superior">University of Wisconsin - Superior</option>
                      <option value="University of Wisconsin - Whitewater">University of Wisconsin - Whitewater</option>
                      <option value="University of Wyoming">University of Wyoming</option>
                      <option value="Upper Iowa University">Upper Iowa University</option>
                      <option value="Urbana University">Urbana University</option>
                      <option value="Ursinus College">Ursinus College</option>
                      <option value="Ursuline College">Ursuline College</option>
                      <option value="Utah State University">Utah State University</option>
                      <option value="Utah Valley State College">Utah Valley State College</option>
                      <option value="Utica College">Utica College</option>
                      <option value="Valdosta State University">Valdosta State University</option>
                      <option value="Valley City State University">Valley City State University</option>
                      <option value="Valparaiso University">Valparaiso University</option>
                      <option value="Vanderbilt University">Vanderbilt University</option>
                      <option value="Vanguard University of Southern California">Vanguard University of Southern California</option>
                      <option value="Vassar College">Vassar College</option>
                      <option value="Vermont Technical College">Vermont Technical College</option>
                      <option value="Villa Julie College">Villa Julie College</option>
                      <option value="Villanova University">Villanova University</option>
                      <option value="Virginia Commonwealth University">Virginia Commonwealth University</option>
                      <option value="Virginia Intermont College">Virginia Intermont College</option>
                      <option value="Virginia Military Institute">Virginia Military Institute</option>
                      <option value="Virginia Polytechnic Institute and State University">Virginia Polytechnic Institute and State University</option>
                      <option value="Virginia State University">Virginia State University</option>
                      <option value="Virginia Union University">Virginia Union University</option>
                      <option value="Virginia Wesleyan College">Virginia Wesleyan College</option>
                      <option value="Viterbo University">Viterbo University</option>
                      <option value="Voorhees College">Voorhees College</option>
                      <option value="Wabash College">Wabash College</option>
                      <option value="Wagner College">Wagner College</option>
                      <option value="Wake Forest University">Wake Forest University</option>
                      <option value="Waldorf College">Waldorf College</option>
                      <option value="Walla Walla College">Walla Walla College</option>
                      <option value="Walsh University">Walsh University</option>
                      <option value="Warner Pacific College">Warner Pacific College</option>
                      <option value="Warner Southern College">Warner Southern College</option>
                      <option value="Warren Wilson College">Warren Wilson College</option>
                      <option value="Wartburg College">Wartburg College</option>
                      <option value="Washburn University of Topeka">Washburn University of Topeka</option>
                      <option value="Washington and Jefferson College">Washington and Jefferson College</option>
                      <option value="Washington and Lee University">Washington and Lee University</option>
                      <option value="Washington College">Washington College</option>
                      <option value="Washington State University">Washington State University</option>
                      <option value="Washington University in St. Louis">Washington University in St. Louis</option>
                      <option value="Wayland Baptist University">Wayland Baptist University</option>
                      <option value="Waynesburg College">Waynesburg College</option>
                      <option value="Wayne State College">Wayne State College</option>
                      <option value="Wayne State University">Wayne State University</option>
                      <option value="Webber International University">Webber International University</option>
                      <option value="Weber State University">Weber State University</option>
                      <option value="Webster University">Webster University</option>
                      <option value="Wellesley College">Wellesley College</option>
                      <option value="Wells College">Wells College</option>
                      <option value="Wentworth Institute of Technology">Wentworth Institute of Technology</option>
                      <option value="Wesleyan College">Wesleyan College</option>
                      <option value="Wesleyan University">Wesleyan University</option>
                      <option value="Wesley College">Wesley College</option>
                      <option value="West Chester University of Pennsylvania">West Chester University of Pennsylvania</option>
                      <option value="Western Carolina University">Western Carolina University</option>
                      <option value="Western Connecticut State University">Western Connecticut State University</option>
                      <option value="Western Governors University">Western Governors University</option>
                      <option value="Western Illinois University">Western Illinois University</option>
                      <option value="Western Kentucky University">Western Kentucky University</option>
                      <option value="Western Michigan University">Western Michigan University</option>
                      <option value="Western New England College">Western New England College</option>
                      <option value="Western Oregon University">Western Oregon University</option>
                      <option value="Western State College of Colorado">Western State College of Colorado</option>
                      <option value="Western Washington University">Western Washington University</option>
                      <option value="Westfield State College">Westfield State College</option>
                      <option value="West Liberty State College">West Liberty State College</option>
                      <option value="Westminster College Missouri">Westminster College Missouri</option>
                      <option value="Westminster College Pennsylvania">Westminster College Pennsylvania</option>
                      <option value="Westminster College Utah">Westminster College Utah</option>
                      <option value="Westmont College">Westmont College</option>
                      <option value="West Texas A&M University">West Texas A&M University</option>
                      <option value="West Virginia State University">West Virginia State University</option>
                      <option value="West Virginia University">West Virginia University</option>
                      <option value="West Virginia University Institute of Technology">West Virginia University Institute of Technology</option>
                      <option value="West Virginia Wesleyan College">West Virginia Wesleyan College</option>
                      <option value="Wheaton College Illinois">Wheaton College Illinois</option>
                      <option value="Wheaton College Massachusetts">Wheaton College Massachusetts</option>
                      <option value="Wheeling Jesuit University">Wheeling Jesuit University</option>
                      <option value="Wheelock College">Wheelock College</option>
                      <option value="Whitman College">Whitman College</option>
                      <option value="Whittier College">Whittier College</option>
                      <option value="Whitworth College">Whitworth College</option>
                      <option value="Wichita State University">Wichita State University</option>
                      <option value="Widener University">Widener University</option>
                      <option value="Wilberforce University">Wilberforce University</option>
                      <option value="Wiley College">Wiley College</option>
                      <option value="Wilkes University">Wilkes University</option>
                      <option value="Willamette University">Willamette University</option>
                      <option value="William Carey College">William Carey College</option>
                      <option value="William Jewell College">William Jewell College</option>
                      <option value="William Paterson University of New Jersey">William Paterson University of New Jersey</option>
                      <option value="William Penn University">William Penn University</option>
                      <option value="Williams College">Williams College</option>
                      <option value="William Woods University">William Woods University</option>
                      <option value="Wilmington College">Wilmington College</option>
                      <option value="Wilson College">Wilson College</option>
                      <option value="Wingate University">Wingate University</option>
                      <option value="Winona State University">Winona State University</option>
                      <option value="Winston-Salem State University">Winston-Salem State University</option>
                      <option value="Winthrop University">Winthrop University</option>
                      <option value="Wisconsin Lutheran College">Wisconsin Lutheran College</option>
                      <option value="Wittenberg University">Wittenberg University</option>
                      <option value="Wofford College">Wofford College</option>
                      <option value="Woodbury University">Woodbury University</option>
                      <option value="Worcester Polytechnic Institute">Worcester Polytechnic Institute</option>
                      <option value="Worcester State College">Worcester State College</option>
                      <option value="Wright State University">Wright State University</option>
                      <option value="Xavier University">Xavier University</option>
                      <option value="Xavier University of Louisiana">Xavier University of Louisiana</option>
                      <option value="Yale University">Yale University</option>
                      <option value="Yeshiva University">Yeshiva University</option>
                      <option value="York College Nebraska">York College Nebraska</option>
                      <option value="York College of Pennsylvania">York College of Pennsylvania</option>
                      <option value="Youngstown State University">Youngstown State University</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Academic Information */}
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Academic Information (Optional)</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Major</label>
                    <select
                      value={singleStudentForm.major}
                      onChange={(e) => setSingleStudentForm({...singleStudentForm, major: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="">Select Major...</option>
                      <option value="3-D Modeling and Design Technology / Technician">3-D Modeling and Design Technology / Technician</option>
                      <option value="Acoustics">Acoustics</option>
                      <option value="Advanced Materials">Advanced Materials</option>
                      <option value="Aeronautical / Aerospace Engineering Technology / Technician">Aeronautical / Aerospace Engineering Technology / Technician</option>
                      <option value="Aeronautical and Astronautical Engineering">Aeronautical and Astronautical Engineering</option>
                      <option value="Aeronautical Engineering">Aeronautical Engineering</option>
                      <option value="Aerospace, Aeronautical, and Astronautical / Space Engineering, General">Aerospace, Aeronautical, and Astronautical / Space Engineering, General</option>
                      <option value="Aerospace, Aeronautical, and Astronautical / Space Engineering, Other">Aerospace, Aeronautical, and Astronautical / Space Engineering, Other</option>
                      <option value="Aerospace Engineering">Aerospace Engineering</option>
                      <option value="Aerospace Physiology and Medicine">Aerospace Physiology and Medicine</option>
                      <option value="Agricultural Engineering">Agricultural Engineering</option>
                      <option value="Agriculture, Food & Life Sciences">Agriculture, Food & Life Sciences</option>
                      <option value="Algebra and Number Theory">Algebra and Number Theory</option>
                      <option value="Analysis and Functional Analysis">Analysis and Functional Analysis</option>
                      <option value="Analytical Chemistry">Analytical Chemistry</option>
                      <option value="Anatomy">Anatomy</option>
                      <option value="Animal Behavior and Ethology">Animal Behavior and Ethology</option>
                      <option value="Animal Genetics">Animal Genetics</option>
                      <option value="Animal Physiology">Animal Physiology</option>
                      <option value="Anthropology">Anthropology</option>
                      <option value="Applied Biosciences">Applied Biosciences</option>
                      <option value="Applied Engineering">Applied Engineering</option>
                      <option value="Applied Engineering Technologies / Technicians">Applied Engineering Technologies / Technicians</option>
                      <option value="Applied Mathematics, General">Applied Mathematics, General</option>
                      <option value="Applied Mathematics, Other">Applied Mathematics, Other</option>
                      <option value="Applied Physics">Applied Physics</option>
                      <option value="Applied Statistics, General">Applied Statistics, General</option>
                      <option value="Aquatic Biology / Limnology">Aquatic Biology / Limnology</option>
                      <option value="Architectural Drafting and Architectural CAD/CADD">Architectural Drafting and Architectural CAD/CADD</option>
                      <option value="Architectural Engineering">Architectural Engineering</option>
                      <option value="Architectural Engineering Technologies / Technicians">Architectural Engineering Technologies / Technicians</option>
                      <option value="Astronautical Engineering">Astronautical Engineering</option>
                      <option value="Astronomy">Astronomy</option>
                      <option value="Astronomy and Astrophysics, Other">Astronomy and Astrophysics, Other</option>
                      <option value="Astrophysics">Astrophysics</option>
                      <option value="Atmospheric Chemistry and Climatology">Atmospheric Chemistry and Climatology</option>
                      <option value="Atmospheric Physics and Dynamics">Atmospheric Physics and Dynamics</option>
                      <option value="Atmospheric Sciences and Meteorology, General">Atmospheric Sciences and Meteorology, General</option>
                      <option value="Atmospheric Sciences and Meteorology, Other">Atmospheric Sciences and Meteorology, Other</option>
                      <option value="Atomic / Molecular Physics">Atomic / Molecular Physics</option>
                      <option value="Audio Engineering Technology / Technician">Audio Engineering Technology / Technician</option>
                      <option value="Automation Engineering">Automation Engineering</option>
                      <option value="Automation Engineer Technology / Technician">Automation Engineer Technology / Technician</option>
                      <option value="Automotive Engineering Technology / Technician">Automotive Engineering Technology / Technician</option>
                      <option value="Biochemical Engineering">Biochemical Engineering</option>
                      <option value="Biochemistry">Biochemistry</option>
                      <option value="Biochemistry, Biophysics and Molecular Biology, Other">Biochemistry, Biophysics and Molecular Biology, Other</option>
                      <option value="Biochemistry and Molecular Biology">Biochemistry and Molecular Biology</option>
                      <option value="Bioengineering">Bioengineering</option>
                      <option value="Bioengineering and Biomedical Engineering">Bioengineering and Biomedical Engineering</option>
                      <option value="Bioinformatics">Bioinformatics</option>
                      <option value="Biological / Biosystems Engineering">Biological / Biosystems Engineering</option>
                      <option value="Biological and Biomedical Sciences, Other">Biological and Biomedical Sciences, Other</option>
                      <option value="Biological Engineering">Biological Engineering</option>
                      <option value="Biology">Biology</option>
                      <option value="Biology / Biological Sciences, General">Biology / Biological Sciences, General</option>
                      <option value="Biomathematics, Bioinformatics, and Computational Biology, Other">Biomathematics, Bioinformatics, and Computational Biology, Other</option>
                      <option value="Biomechanics">Biomechanics</option>
                      <option value="Biomedical & Biological Sciences">Biomedical & Biological Sciences</option>
                      <option value="Biomedical Engineering">Biomedical Engineering</option>
                      <option value="Biomedical Sciences, General">Biomedical Sciences, General</option>
                      <option value="Biomedical Technology / Technician">Biomedical Technology / Technician</option>
                      <option value="Biometry / Biometrics">Biometry / Biometrics</option>
                      <option value="Biophysics">Biophysics</option>
                      <option value="Biostatistics">Biostatistics</option>
                      <option value="Biosystems Engineering">Biosystems Engineering</option>
                      <option value="Biotechnology">Biotechnology</option>
                      <option value="Botany / Plant Biology">Botany / Plant Biology</option>
                      <option value="Botany / Plant Biology, Other">Botany / Plant Biology, Other</option>
                      <option value="Business Administration and Management">Business Administration and Management</option>
                      <option value="CAD/CADD Drafting and/or Design Technology / Technician">CAD/CADD Drafting and/or Design Technology / Technician</option>
                      <option value="Cardiovascular Science">Cardiovascular Science</option>
                      <option value="Cell / Cellular and Molecular Biology">Cell / Cellular and Molecular Biology</option>
                      <option value="Cell / Cellular Biology and Anatomical Sciences, Other">Cell / Cellular Biology and Anatomical Sciences, Other</option>
                      <option value="Cell / Cellular Biology and Histology">Cell / Cellular Biology and Histology</option>
                      <option value="Cell Biology and Anatomy">Cell Biology and Anatomy</option>
                      <option value="Cell Physiology">Cell Physiology</option>
                      <option value="Ceramic Sciences and Engineering">Ceramic Sciences and Engineering</option>
                      <option value="Chemical and Biological Engineering">Chemical and Biological Engineering</option>
                      <option value="Chemical and Biomolecular Engineering">Chemical and Biomolecular Engineering</option>
                      <option value="Chemical Engineering">Chemical Engineering</option>
                      <option value="Chemical Engineering, Other">Chemical Engineering, Other</option>
                      <option value="Chemical Engineering Technology / Technician">Chemical Engineering Technology / Technician</option>
                      <option value="Chemical Physics">Chemical Physics</option>
                      <option value="Cheminformatics / Chemistry Informatics">Cheminformatics / Chemistry Informatics</option>
                      <option value="Chemistry, General">Chemistry, General</option>
                      <option value="Chemistry, Other">Chemistry, Other</option>
                      <option value="Civil and Environmental Engineering">Civil and Environmental Engineering</option>
                      <option value="Civil Drafting and Civil Engineering CAD/CADD">Civil Drafting and Civil Engineering CAD/CADD</option>
                      <option value="Civil Engineering">Civil Engineering</option>
                      <option value="Civil Engineering, General">Civil Engineering, General</option>
                      <option value="Civil Engineering, Other">Civil Engineering, Other</option>
                      <option value="Civil Engineering Technologies / Technicians">Civil Engineering Technologies / Technicians</option>
                      <option value="Communications">Communications</option>
                      <option value="Communications Engineering">Communications Engineering</option>
                      <option value="Composite Materials Technology / Technician">Composite Materials Technology / Technician</option>
                      <option value="Computational and Applied Mathematics">Computational and Applied Mathematics</option>
                      <option value="Computational Biology">Computational Biology</option>
                      <option value="Computational Mathematics">Computational Mathematics</option>
                      <option value="Computer / Computer Systems Technology / Technician">Computer / Computer Systems Technology / Technician</option>
                      <option value="Computer Engineering">Computer Engineering</option>
                      <option value="Computer Engineering, General">Computer Engineering, General</option>
                      <option value="Computer Engineering, Other">Computer Engineering, Other</option>
                      <option value="Computer Engineering Technologies / Technicians, Other">Computer Engineering Technologies / Technicians, Other</option>
                      <option value="Computer Engineering Technology / Technician">Computer Engineering Technology / Technician</option>
                      <option value="Computer Hardware Engineering">Computer Hardware Engineering</option>
                      <option value="Computer Hardware Technology / Technician">Computer Hardware Technology / Technician</option>
                      <option value="Computer Programming">Computer Programming</option>
                      <option value="Computer Science">Computer Science</option>
                      <option value="Computer Software Engineering">Computer Software Engineering</option>
                      <option value="Computer Software Technology / Technician">Computer Software Technology / Technician</option>
                      <option value="Condensed Matter and Materials Physics">Condensed Matter and Materials Physics</option>
                      <option value="Conservation Biology">Conservation Biology</option>
                      <option value="Construction Engineering">Construction Engineering</option>
                      <option value="Construction Engineering Technology / Technician">Construction Engineering Technology / Technician</option>
                      <option value="Data Science, General">Data Science, General</option>
                      <option value="Developmental Biology and Embryology">Developmental Biology and Embryology</option>
                      <option value="Drafting and Design Technology / Technician, General">Drafting and Design Technology / Technician, General</option>
                      <option value="Drafting and Design Technology / Technician, Other">Drafting and Design Technology / Technician, Other</option>
                      <option value="Ecology">Ecology</option>
                      <option value="Ecology, Evolution, Systematics and Population Biology, Other">Ecology, Evolution, Systematics and Population Biology, Other</option>
                      <option value="Ecology and Evolutionary Biology">Ecology and Evolutionary Biology</option>
                      <option value="Education">Education</option>
                      <option value="Electrical, Electronic, and Communications Engineering Technology / Technician">Electrical, Electronic, and Communications Engineering Technology / Technician</option>
                      <option value="Electrical, Electronics, and Communications Engineering, Other">Electrical, Electronics, and Communications Engineering, Other</option>
                      <option value="Electrical / Electronic Engineering Technologies / Technicians, Other">Electrical / Electronic Engineering Technologies / Technicians, Other</option>
                      <option value="Electrical / Electronics Drafting and Electrical/Electronics CAD/CADD">Electrical / Electronics Drafting and Electrical/Electronics CAD/CADD</option>
                      <option value="Electrical and Computer Engineering">Electrical and Computer Engineering</option>
                      <option value="Electrical and Electronics Engineering">Electrical and Electronics Engineering</option>
                      <option value="Electrical Engineering">Electrical Engineering</option>
                      <option value="Electrical Engineering & Computer Science">Electrical Engineering & Computer Science</option>
                      <option value="Electromechanical / Electromechanical Engineering Technology / Technician">Electromechanical / Electromechanical Engineering Technology / Technician</option>
                      <option value="Electromechanical Engineering">Electromechanical Engineering</option>
                      <option value="Electromechanical Technologies / Technicians, Other">Electromechanical Technologies / Technicians, Other</option>
                      <option value="Electronics Engineering">Electronics Engineering</option>
                      <option value="Elementary Particle Physics">Elementary Particle Physics</option>
                      <option value="Endocrinology">Endocrinology</option>
                      <option value="Energy Systems Engineering">Energy Systems Engineering</option>
                      <option value="Energy Systems Engineering, General">Energy Systems Engineering, General</option>
                      <option value="Energy Systems Engineering, Other">Energy Systems Engineering, Other</option>
                      <option value="Energy Systems Technologies / Technicians, Other">Energy Systems Technologies / Technicians, Other</option>
                      <option value="Energy Systems Technology / Technician">Energy Systems Technology / Technician</option>
                      <option value="Engineering, Other">Engineering, Other</option>
                      <option value="Engineering / Engineering-Related Technologies / Technicians, Other">Engineering / Engineering-Related Technologies / Technicians, Other</option>
                      <option value="Engineering / Industrial Management">Engineering / Industrial Management</option>
                      <option value="Engineering Chemistry">Engineering Chemistry</option>
                      <option value="Engineering Design">Engineering Design</option>
                      <option value="Engineering Education">Engineering Education</option>
                      <option value="Engineering - General">Engineering - General</option>
                      <option value="Engineering Mechanics">Engineering Mechanics</option>
                      <option value="Engineering - Other">Engineering - Other</option>
                      <option value="Engineering Physics">Engineering Physics</option>
                      <option value="Engineering Physics / Applied Physics">Engineering Physics / Applied Physics</option>
                      <option value="Engineering Project Management">Engineering Project Management</option>
                      <option value="Engineering-Related Fields, Other">Engineering-Related Fields, Other</option>
                      <option value="Engineering-Related Technologies / Technicians, Other">Engineering-Related Technologies / Technicians, Other</option>
                      <option value="Engineering Science">Engineering Science</option>
                      <option value="Engineering Technologies / Technicians, General">Engineering Technologies / Technicians, General</option>
                      <option value="Entomology">Entomology</option>
                      <option value="Environmental / Environmental Engineering Technology / Technician">Environmental / Environmental Engineering Technology / Technician</option>
                      <option value="Environmental/Environmental Health Engineering">Environmental/Environmental Health Engineering</option>
                      <option value="Environmental / Environmental Health Engineering">Environmental / Environmental Health Engineering</option>
                      <option value="Environmental & Water Resources Science">Environmental & Water Resources Science</option>
                      <option value="Environmental Biology">Environmental Biology</option>
                      <option value="Environmental Chemistry">Environmental Chemistry</option>
                      <option value="Environmental Control Technologies / Technicians, Other">Environmental Control Technologies / Technicians, Other</option>
                      <option value="Environmental Engineering">Environmental Engineering</option>
                      <option value="Environmental Science">Environmental Science</option>
                      <option value="Environmental Studies">Environmental Studies</option>
                      <option value="Environmental Toxicology">Environmental Toxicology</option>
                      <option value="Epidemiology">Epidemiology</option>
                      <option value="Epidemiology and Biostatistics">Epidemiology and Biostatistics</option>
                      <option value="Evolutionary Biology">Evolutionary Biology</option>
                      <option value="Exercise Physiology and Kinesiology">Exercise Physiology and Kinesiology</option>
                      <option value="Financial Mathematics">Financial Mathematics</option>
                      <option value="Fisheries">Fisheries</option>
                      <option value="Food & Nutrition">Food & Nutrition</option>
                      <option value="Forensic Anthropology">Forensic Anthropology</option>
                      <option value="Forensic Chemistry">Forensic Chemistry</option>
                      <option value="Forest Engineering">Forest Engineering</option>
                      <option value="Forestry">Forestry</option>
                      <option value="Genetics, General">Genetics, General</option>
                      <option value="Genetics, Other">Genetics, Other</option>
                      <option value="Genome Sciences / Genomics">Genome Sciences / Genomics</option>
                      <option value="Geochemistry">Geochemistry</option>
                      <option value="Geochemistry and Petrology">Geochemistry and Petrology</option>
                      <option value="Geography and Atmospheric Sciences">Geography and Atmospheric Sciences</option>
                      <option value="Geography and Environmental Systems">Geography and Environmental Systems</option>
                      <option value="Geological/Geophysical Engineering">Geological/Geophysical Engineering</option>
                      <option value="Geological / Geophysical Engineering">Geological / Geophysical Engineering</option>
                      <option value="Geological and Earth Sciences / Geosciences, Other">Geological and Earth Sciences / Geosciences, Other</option>
                      <option value="Geology / Earth Science, General">Geology / Earth Science, General</option>
                      <option value="Geometry / Geometric Analysis">Geometry / Geometric Analysis</option>
                      <option value="Geophysics and Seismology">Geophysics and Seismology</option>
                      <option value="Geotechnical and Geoenvironmental Engineering">Geotechnical and Geoenvironmental Engineering</option>
                      <option value="Geothermal Energy Technology / Technician">Geothermal Energy Technology / Technician</option>
                      <option value="Gerontology">Gerontology</option>
                      <option value="Hazardous Materials Information Systems Technology / Technician">Hazardous Materials Information Systems Technology / Technician</option>
                      <option value="Hazardous Materials Management and Waste Technology / Technician">Hazardous Materials Management and Waste Technology / Technician</option>
                      <option value="Health Science">Health Science</option>
                      <option value="Heating, Ventilation, Air Conditioning and Refrigeration Engineering Technology / Technician">Heating, Ventilation, Air Conditioning and Refrigeration Engineering Technology / Technician</option>
                      <option value="History">History</option>
                      <option value="Human / Medical Genetics">Human / Medical Genetics</option>
                      <option value="Human Development">Human Development</option>
                      <option value="Hydraulics and Fluid Power Technology / Technician">Hydraulics and Fluid Power Technology / Technician</option>
                      <option value="Hydroelectric Energy Technology / Technician">Hydroelectric Energy Technology / Technician</option>
                      <option value="Hydrology and Water Resources Science">Hydrology and Water Resources Science</option>
                      <option value="Immunology">Immunology</option>
                      <option value="Industrial/System Engineering">Industrial/System Engineering</option>
                      <option value="Industrial/Systems/Operations Research Engineering">Industrial/Systems/Operations Research Engineering</option>
                      <option value="Industrial Engineering">Industrial Engineering</option>
                      <option value="Industrial Production Technologies / Technicians">Industrial Production Technologies / Technicians</option>
                      <option value="Industrial Safety Technology / Technician">Industrial Safety Technology / Technician</option>
                      <option value="Industrial Technology / Technician">Industrial Technology / Technician</option>
                      <option value="Infectious Disease and Global Health">Infectious Disease and Global Health</option>
                      <option value="Information Technology">Information Technology</option>
                      <option value="Inorganic Chemistry">Inorganic Chemistry</option>
                      <option value="Instrumentation Technology / Technician">Instrumentation Technology / Technician</option>
                      <option value="Integrated Circuit Design Technology / Technician">Integrated Circuit Design Technology / Technician</option>
                      <option value="Integrated Design of Electromagnetically-Applied Systems">Integrated Design of Electromagnetically-Applied Systems</option>
                      <option value="International Studies">International Studies</option>
                      <option value="Kinesiology">Kinesiology</option>
                      <option value="Laser and Optical Engineering">Laser and Optical Engineering</option>
                      <option value="Laser and Optical Technology / Technician">Laser and Optical Technology / Technician</option>
                      <option value="Liberal Arts">Liberal Arts</option>
                      <option value="Management Science & Operations Research">Management Science & Operations Research</option>
                      <option value="Manufacturing Engineering">Manufacturing Engineering</option>
                      <option value="Manufacturing Engineering Technology / Technician">Manufacturing Engineering Technology / Technician</option>
                      <option value="Marine Biology and Biological Oceanography">Marine Biology and Biological Oceanography</option>
                      <option value="Marine Engineering Technology / Technician">Marine Engineering Technology / Technician</option>
                      <option value="Materials Chemistry">Materials Chemistry</option>
                      <option value="Materials Engineering">Materials Engineering</option>
                      <option value="Materials Science">Materials Science</option>
                      <option value="Materials Science and Engineering">Materials Science and Engineering</option>
                      <option value="Materials Sciences, Other">Materials Sciences, Other</option>
                      <option value="Mathematical Biology">Mathematical Biology</option>
                      <option value="Mathematical Statistics and Probability">Mathematical Statistics and Probability</option>
                      <option value="Mathematics, General">Mathematics, General</option>
                      <option value="Mathematics, Other">Mathematics, Other</option>
                      <option value="Mathematics and Statistics">Mathematics and Statistics</option>
                      <option value="Mathematics and Statistics, Other">Mathematics and Statistics, Other</option>
                      <option value="Mechanical / Mechanical Engineering Technology / Technician">Mechanical / Mechanical Engineering Technology / Technician</option>
                      <option value="Mechanical Drafting and Mechanical Drafting CAD/CADD">Mechanical Drafting and Mechanical Drafting CAD/CADD</option>
                      <option value="Mechanical Engineering">Mechanical Engineering</option>
                      <option value="Mechanical Engineering Related Technologies / Technicians, Other">Mechanical Engineering Related Technologies / Technicians, Other</option>
                      <option value="Mechatronics, Robotics, and Automation Engineering">Mechatronics, Robotics, and Automation Engineering</option>
                      <option value="Mechatronics, Robotics, and Automation Engineering Technology / Technician">Mechatronics, Robotics, and Automation Engineering Technology / Technician</option>
                      <option value="Mechatronics Engineering">Mechatronics Engineering</option>
                      <option value="Medical Engineering">Medical Engineering</option>
                      <option value="Medical Microbiology and Bacteriology">Medical Microbiology and Bacteriology</option>
                      <option value="Medical Pharmacology">Medical Pharmacology</option>
                      <option value="Metallurgical Engineering">Metallurgical Engineering</option>
                      <option value="Metallurgical Technology / Technician">Metallurgical Technology / Technician</option>
                      <option value="Meteorology">Meteorology</option>
                      <option value="Meterology and Atmospheric Science">Meterology and Atmospheric Science</option>
                      <option value="Microbial and Eukaryotic Genetics">Microbial and Eukaryotic Genetics</option>
                      <option value="Microbiological Sciences and Immunology, Other">Microbiological Sciences and Immunology, Other</option>
                      <option value="Microbiology, General">Microbiology, General</option>
                      <option value="Microbiology and Immunology">Microbiology and Immunology</option>
                      <option value="Mining and Mineral Engineering">Mining and Mineral Engineering</option>
                      <option value="Mining and Petroleum Technologies / Technicians, Other">Mining and Petroleum Technologies / Technicians, Other</option>
                      <option value="Mining Technology / Technician">Mining Technology / Technician</option>
                      <option value="Molecular Biochemistry">Molecular Biochemistry</option>
                      <option value="Molecular Biology">Molecular Biology</option>
                      <option value="Molecular Biophysics">Molecular Biophysics</option>
                      <option value="Molecular Genetics">Molecular Genetics</option>
                      <option value="Molecular Medicine">Molecular Medicine</option>
                      <option value="Molecular Pharmacology">Molecular Pharmacology</option>
                      <option value="Molecular Physiology">Molecular Physiology</option>
                      <option value="Molecular Plant Pathology">Molecular Plant Pathology</option>
                      <option value="Molecular Toxicology">Molecular Toxicology</option>
                      <option value="Motorsports Engineering Technology / Technician">Motorsports Engineering Technology / Technician</option>
                      <option value="Multimedia Web Design & Development">Multimedia Web Design & Development</option>
                      <option value="Mycology">Mycology</option>
                      <option value="Nanoengineering">Nanoengineering</option>
                      <option value="Nanotechnology">Nanotechnology</option>
                      <option value="Native American Studies">Native American Studies</option>
                      <option value="Natural Resources and Conservation">Natural Resources and Conservation</option>
                      <option value="Natural Sciences">Natural Sciences</option>
                      <option value="Naval Architecture and Marine Engineering">Naval Architecture and Marine Engineering</option>
                      <option value="Near Eastern Studies">Near Eastern Studies</option>
                      <option value="Neuroanatomy">Neuroanatomy</option>
                      <option value="Neurobiology and Anatomy">Neurobiology and Anatomy</option>
                      <option value="Neurobiology and Behavior">Neurobiology and Behavior</option>
                      <option value="Neurobiology and Neurosciences, Other">Neurobiology and Neurosciences, Other</option>
                      <option value="Neuropharmacology">Neuropharmacology</option>
                      <option value="Neuroscience">Neuroscience</option>
                      <option value="Not Declared - Freshman">Not Declared - Freshman</option>
                      <option value="Not Declared - Upperclassman">Not Declared - Upperclassman</option>
                      <option value="Nuclear Engineering">Nuclear Engineering</option>
                      <option value="Nuclear Engineering Technology / Technician">Nuclear Engineering Technology / Technician</option>
                      <option value="Nuclear Physics">Nuclear Physics</option>
                      <option value="Nutritional Sciences">Nutritional Sciences</option>
                      <option value="Occupational Safety and Health Technology / Technician">Occupational Safety and Health Technology / Technician</option>
                      <option value="Ocean Engineering">Ocean Engineering</option>
                      <option value="Oceanography, Chemical and Physical">Oceanography, Chemical and Physical</option>
                      <option value="Oncology and Cancer Biology">Oncology and Cancer Biology</option>
                      <option value="Operations Research">Operations Research</option>
                      <option value="Optics / Optical Sciences">Optics / Optical Sciences</option>
                      <option value="Organic Chemistry">Organic Chemistry</option>
                      <option value="Packaging Science">Packaging Science</option>
                      <option value="Paleontology">Paleontology</option>
                      <option value="Paper Science and Engineering">Paper Science and Engineering</option>
                      <option value="Parasitology">Parasitology</option>
                      <option value="Pathology / Experimental Pathology">Pathology / Experimental Pathology</option>
                      <option value="Petroleum Engineering">Petroleum Engineering</option>
                      <option value="Petroleum Technology / Technician">Petroleum Technology / Technician</option>
                      <option value="Pharmacology">Pharmacology</option>
                      <option value="Pharmacology and Toxicology">Pharmacology and Toxicology</option>
                      <option value="Pharmacology and Toxicology, Other">Pharmacology and Toxicology, Other</option>
                      <option value="Photobiology">Photobiology</option>
                      <option value="Physical Chemistry">Physical Chemistry</option>
                      <option value="Physical Sciences, General">Physical Sciences, General</option>
                      <option value="Physical Sciences, Other">Physical Sciences, Other</option>
                      <option value="Physics">Physics</option>
                      <option value="Physics, General">Physics, General</option>
                      <option value="Physics, Other">Physics, Other</option>
                      <option value="Physics and Astronomy">Physics and Astronomy</option>
                      <option value="Physiology, General">Physiology, General</option>
                      <option value="Physiology, Pathology, and Related Sciences, Other">Physiology, Pathology, and Related Sciences, Other</option>
                      <option value="Planetary Astronomy and Science">Planetary Astronomy and Science</option>
                      <option value="Plant Genetics">Plant Genetics</option>
                      <option value="Plant Molecular Biology">Plant Molecular Biology</option>
                      <option value="Plant Pathology / Phytopathology">Plant Pathology / Phytopathology</option>
                      <option value="Plant Physiology">Plant Physiology</option>
                      <option value="Plasma and High-Temperature Physics">Plasma and High-Temperature Physics</option>
                      <option value="Plastics and Polymer Engineering Technology / Technician">Plastics and Polymer Engineering Technology / Technician</option>
                      <option value="Polymer/Plastics Engineering">Polymer/Plastics Engineering</option>
                      <option value="Polymer / Plastics Engineering">Polymer / Plastics Engineering</option>
                      <option value="Polymer Chemistry">Polymer Chemistry</option>
                      <option value="Population Biology">Population Biology</option>
                      <option value="Power Plant Engineering">Power Plant Engineering</option>
                      <option value="Power Plant Technology / Technician">Power Plant Technology / Technician</option>
                      <option value="Pre-Engineering">Pre-Engineering</option>
                      <option value="Premedicine">Premedicine</option>
                      <option value="Process Safety Technology / Technician">Process Safety Technology / Technician</option>
                      <option value="Psychology">Psychology</option>
                      <option value="Public Administration">Public Administration</option>
                      <option value="Public Health">Public Health</option>
                      <option value="Quality Control and Safety Technologies / Technicians, Other">Quality Control and Safety Technologies / Technicians, Other</option>
                      <option value="Quality Control Technology / Technician">Quality Control Technology / Technician</option>
                      <option value="Radiation Biology / Radiobiology">Radiation Biology / Radiobiology</option>
                      <option value="Reproductive Biology">Reproductive Biology</option>
                      <option value="Robotics Engineering">Robotics Engineering</option>
                      <option value="Robotics Technology / Technician">Robotics Technology / Technician</option>
                      <option value="Science - Biological">Science - Biological</option>
                      <option value="Science - Physical">Science - Physical</option>
                      <option value="Semiconductor Manufacturing Technology / Technician">Semiconductor Manufacturing Technology / Technician</option>
                      <option value="Software Engineering">Software Engineering</option>
                      <option value="Soil, Water & Environmental Science">Soil, Water & Environmental Science</option>
                      <option value="Solar Energy Technology / Technician">Solar Energy Technology / Technician</option>
                      <option value="Space Engineering">Space Engineering</option>
                      <option value="Statistics, General">Statistics, General</option>
                      <option value="Statistics, Other">Statistics, Other</option>
                      <option value="Structural Biology">Structural Biology</option>
                      <option value="Structural Engineering">Structural Engineering</option>
                      <option value="Surveying Engineering">Surveying Engineering</option>
                      <option value="Surveying Technology / Surveying">Surveying Technology / Surveying</option>
                      <option value="Systematic Biology / Biological Systematics">Systematic Biology / Biological Systematics</option>
                      <option value="Systems Engineering">Systems Engineering</option>
                      <option value="Technology">Technology</option>
                      <option value="Telecommunications Engineering">Telecommunications Engineering</option>
                      <option value="Telecommunications Technology / Technician">Telecommunications Technology / Technician</option>
                      <option value="Textile Sciences and Engineering">Textile Sciences and Engineering</option>
                      <option value="Theoretical and Mathematical Physics">Theoretical and Mathematical Physics</option>
                      <option value="Theoretical Chemistry">Theoretical Chemistry</option>
                      <option value="Topology and Foundations">Topology and Foundations</option>
                      <option value="Toxicology">Toxicology</option>
                      <option value="Transportation and Highway Engineering">Transportation and Highway Engineering</option>
                      <option value="Virology">Virology</option>
                      <option value="Vision Science / Physiological Optics">Vision Science / Physiological Optics</option>
                      <option value="Water Quality and Wastewater Treatment Management and Recycling Technology / Technician">Water Quality and Wastewater Treatment Management and Recycling Technology / Technician</option>
                      <option value="Water Resources Engineering">Water Resources Engineering</option>
                      <option value="Welding Engineering Technology / Technician">Welding Engineering Technology / Technician</option>
                      <option value="Wildlife Biology">Wildlife Biology</option>
                      <option value="Wind Energy Technology / Technician">Wind Energy Technology / Technician</option>
                      <option value="Zoology / Animal Biology">Zoology / Animal Biology</option>
                      <option value="Zoology / Animal Biology, Other">Zoology / Animal Biology, Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">GPA (0.00-4.00)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max="4"
                      value={singleStudentForm.gpa}
                      onChange={(e) => setSingleStudentForm({...singleStudentForm, gpa: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Classification</label>
                    <select
                      value={singleStudentForm.current_academic_class}
                      onChange={(e) => setSingleStudentForm({...singleStudentForm, current_academic_class: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="">Select...</option>
                      <option value="College - 1st Year">College - 1st Year</option>
                      <option value="College - 2nd Year">College - 2nd Year</option>
                      <option value="College - 3rd Year">College - 3rd Year</option>
                      <option value="College - 4th Year">College - 4th Year</option>
                      <option value="College - 5th Year">College - 5th Year</option>
                      <option value="NA">NA</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Expected Graduation Date</label>
                    <input
                      type="date"
                      value={singleStudentForm.expected_grad_date}
                      onChange={(e) => setSingleStudentForm({...singleStudentForm, expected_grad_date: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Matriculation Date</label>
                    <input
                      type="date"
                      value={singleStudentForm.matriculation_date}
                      onChange={(e) => setSingleStudentForm({...singleStudentForm, matriculation_date: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Applicant Status</label>
                    <select
                      value={singleStudentForm.applicant_status}
                      onChange={(e) => setSingleStudentForm({...singleStudentForm, applicant_status: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="">Select...</option>
                      <option value="Graduated">Graduated</option>
                      <option value="Continuing">Continuing</option>
                      <option value="Health Leave of Absence">Health Leave of Absence</option>
                      <option value="Co-Op Leave of Absence">Co-Op Leave of Absence</option>
                      <option value="Temporary Leave Status">Temporary Leave Status</option>
                      <option value="Disqualification Withdrawal">Disqualification Withdrawal</option>
                      <option value="University Withdrawal">University Withdrawal</option>
                      <option value="External Transfer">External Transfer</option>
                      <option value="Permanent Leave">Permanent Leave</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Scholarship Information */}
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Scholarship Information (Optional)</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Block Grant Scholar</label>
                    <select
                      value={singleStudentForm.block_grant_scholar}
                      onChange={(e) => setSingleStudentForm({...singleStudentForm, block_grant_scholar: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="">Select...</option>
                      <option value="Y">Yes</option>
                      <option value="N">No</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Corporate Scholar</label>
                    <select
                      value={singleStudentForm.corporate_scholar}
                      onChange={(e) => setSingleStudentForm({...singleStudentForm, corporate_scholar: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="">Select...</option>
                      <option value="Y">Yes</option>
                      <option value="N">No</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Special Scholarship</label>
                    <select
                      value={singleStudentForm.special_scholarship}
                      onChange={(e) => setSingleStudentForm({...singleStudentForm, special_scholarship: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="">Select...</option>
                      <option value="Y">Yes</option>
                      <option value="N">No</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">NVB Scholar</label>
                    <select
                      value={singleStudentForm.nvb_scholar}
                      onChange={(e) => setSingleStudentForm({...singleStudentForm, nvb_scholar: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="">Select...</option>
                      <option value="Y">Yes</option>
                      <option value="N">No</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date of Initial Block Term</label>
                    <input
                      type="date"
                      value={singleStudentForm.date_of_initial_block_term}
                      onChange={(e) => setSingleStudentForm({...singleStudentForm, date_of_initial_block_term: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                </div>
              </div>

              {/* Demographics */}
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Demographics (Optional)</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Citizenship Status</label>
                    <select
                      value={singleStudentForm.citizenship_status}
                      onChange={(e) => setSingleStudentForm({...singleStudentForm, citizenship_status: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="">Select...</option>
                      <option value="US CITIZEN">US CITIZEN</option>
                      <option value="NON US CITIZEN (PERMANENT RESIDENT)">NON US CITIZEN (PERMANENT RESIDENT)</option>
                      <option value="NON US CITIZEN (PERMIT)">NON US CITIZEN (PERMIT)</option>
                      <option value="NON US CITIZEN (OTHER)">NON US CITIZEN (OTHER)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Gender</label>
                    <select
                      value={singleStudentForm.gender}
                      onChange={(e) => setSingleStudentForm({...singleStudentForm, gender: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="">Select...</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Nonconforming">Nonconforming</option>
                      <option value="Transgender Male">Transgender Male</option>
                      <option value="Transgender Female">Transgender Female</option>
                      <option value="Non-Binary">Non-Binary</option>
                      <option value="NonDisclose">NonDisclose</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Race</label>
                    <select
                      value={singleStudentForm.race}
                      onChange={(e) => setSingleStudentForm({...singleStudentForm, race: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="">Select...</option>
                      <option value="AA/BLACK">AA/BLACK</option>
                      <option value="AL/ALASKANAT">AL/ALASKANAT</option>
                      <option value="ASIAN">ASIAN</option>
                      <option value="LATINx/o/a">LATINx/o/a</option>
                      <option value="NonDisclose">NonDisclose</option>
                      <option value="PI/NatHawaii">PI/NatHawaii</option>
                      <option value="WHITE">WHITE</option>
                      <option value="MULTI-RACIAL">MULTI-RACIAL</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex gap-4">
                <button
                  type="submit"
                  disabled={singleStudentLoading}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {singleStudentLoading ? (
                    <>
                      <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Adding Student...
                    </>
                  ) : (
                    'Add Student'
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setSingleStudentForm({
                      first_name: '',
                      last_name: '',
                      university_email: '',
                      current_university_verified: '',
                      major: '',
                      expected_grad_date: '',
                      current_academic_class: '',
                      gpa: '',
                      matriculation_date: '',
                      conferred_grad_date: '',
                      date_of_initial_block_term: '',
                      major_at_matriculation: '',
                      applicant_status: '',
                      citizenship_status: '',
                      gender: '',
                      race: '',
                      block_grant_scholar: '',
                      corporate_scholar: '',
                      special_scholarship: '',
                      nvb_scholar: ''
                    });
                    setSingleStudentError(null);
                    setSingleStudentSuccess(null);
                  }}
                  className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Clear Form
                </button>
              </div>
            </form>
          </div>
        )}
      </div>

      {/* Search and Filters */}
      <form onSubmit={handleSearch} className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
          {/* Search */}
          <div className="lg:col-span-3">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Search (Name, Email, or ID)
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search students..."
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            />
          </div>

          {/* University Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              University
            </label>
            <input
              type="text"
              value={universityFilter}
              onChange={(e) => setUniversityFilter(e.target.value)}
              placeholder="Filter by university..."
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            />
          </div>

          {/* Major Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Major
            </label>
            <input
              type="text"
              value={majorFilter}
              onChange={(e) => setMajorFilter(e.target.value)}
              placeholder="Filter by major..."
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            />
          </div>

          {/* Class Year Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Class Year
            </label>
            <select
              value={classYearFilter}
              onChange={(e) => setClassYearFilter(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            >
              <option value="">All Years</option>
              <option value="College - 1st Year">College - 1st Year</option>
              <option value="College - 2nd Year">College - 2nd Year</option>
              <option value="College - 3rd Year">College - 3rd Year</option>
              <option value="College - 4th Year">College - 4th Year</option>
              <option value="College - 5th Year">College - 5th Year</option>
              <option value="NA">NA</option>
            </select>
          </div>

          {/* GPA Range */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Min GPA
            </label>
            <input
              type="number"
              step="0.1"
              min="0"
              max="4"
              value={minGpa}
              onChange={(e) => setMinGpa(e.target.value)}
              placeholder="0.0"
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Max GPA
            </label>
            <input
              type="number"
              step="0.1"
              min="0"
              max="4"
              value={maxGpa}
              onChange={(e) => setMaxGpa(e.target.value)}
              placeholder="4.0"
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            />
          </div>

          {/* Verification Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Verification Status
            </label>
            <select
              value={verificationFilter}
              onChange={(e) => setVerificationFilter(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            >
              <option value="">All</option>
              <option value="verified">Verified</option>
              <option value="unverified">Unverified</option>
            </select>
          </div>

          {/* Application Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Application Status
            </label>
            <select
              value={appliedFilter}
              onChange={(e) => setAppliedFilter(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            >
              <option value="">All</option>
              <option value="true">Applied</option>
              <option value="false">Not Applied</option>
            </select>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3">
          <button
            type="submit"
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Apply Filters
          </button>
          <button
            type="button"
            onClick={handleClearFilters}
            className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
          >
            Clear Filters
          </button>
        </div>
      </form>

      {/* Results Summary */}
      <div className="mb-4 text-sm text-gray-600 dark:text-gray-400">
        Showing {students.length} of {totalStudents} students (Page {currentPage} of {totalPages})
      </div>

      {/* Students Table/Cards */}
      <ResponsiveTable
        data={students}
        renderMobileCard={renderMobileStudentCard}
      >
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                ID
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Email
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                University
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Major
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                GPA
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Class Year
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Status
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {students.map((student) => (
              <tr key={student.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                  {student.id}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <button
                    onClick={() => navigate(`/admin/students/${student.id}`)}
                    className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline"
                  >
                    {student.first_name} {student.last_name}
                  </button>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                  {student.email}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                  {student.university || 'N/A'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                  {student.major || 'N/A'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                  {student.gpa || 'N/A'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                  {student.class_year || 'N/A'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <div className="space-y-1">
                    <span className={`inline-block px-2 py-1 text-xs rounded ${student.is_verified ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                      {student.is_verified ? 'Verified' : 'Unverified'}
                    </span>
                    {student.has_applied && (
                      <span className="inline-block px-2 py-1 text-xs rounded bg-blue-100 text-blue-800 ml-1">
                        Applied
                      </span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </ResponsiveTable>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-700 dark:text-gray-300">Items per page:</label>
            <select
              value={limit}
              onChange={(e) => {
                setLimit(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
            >
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-white"
            >
              Previous
            </button>

            <div className="flex gap-1">
              {[...Array(Math.min(5, totalPages))].map((_, idx) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = idx + 1;
                } else if (currentPage <= 3) {
                  pageNum = idx + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + idx;
                } else {
                  pageNum = currentPage - 2 + idx;
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`px-3 py-1 rounded-lg ${
                      currentPage === pageNum
                        ? 'bg-blue-600 text-white'
                        : 'border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-white'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-white"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* CSV Upload Results Modal */}
      {showNewStudentResults && newStudentUploadResults && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white dark:bg-gray-800">
            {/* Modal Header */}
            <div className="flex items-start justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">CSV Upload Results</h2>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Uploaded by: {newStudentUploadResults.summary.admin}
                </p>
              </div>
              <button
                onClick={() => setShowNewStudentResults(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Summary Statistics */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  {newStudentUploadResults.summary.total_rows}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Total Rows</div>
              </div>
              <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {newStudentUploadResults.summary.successful_submissions}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Successful</div>
              </div>
              <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg">
                <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                  {newStudentUploadResults.summary.failed_submissions}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Failed</div>
              </div>
            </div>

            {/* Failed Uploads */}
            {newStudentUploadResults.failed && newStudentUploadResults.failed.length > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-red-600 dark:text-red-400 mb-3">
                  Failed Uploads ({newStudentUploadResults.failed.length})
                </h3>
                <div className="max-h-64 overflow-y-auto border border-red-200 dark:border-red-800 rounded-lg">
                  <table className="min-w-full divide-y divide-red-200 dark:divide-red-800">
                    <thead className="bg-red-50 dark:bg-red-900/20">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-red-800 dark:text-red-200">Row</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-red-800 dark:text-red-200">Name</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-red-800 dark:text-red-200">Email</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-red-800 dark:text-red-200">University</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-red-800 dark:text-red-200">Errors</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-red-100 dark:divide-red-900">
                      {newStudentUploadResults.failed.map((fail, idx) => (
                        <tr key={idx} className="hover:bg-red-50 dark:hover:bg-red-900/10">
                          <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">{fail.row}</td>
                          <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">{fail.student_name || 'N/A'}</td>
                          <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">{fail.university_email || 'N/A'}</td>
                          <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">{fail.university || 'N/A'}</td>
                          <td className="px-4 py-2 text-sm text-red-600 dark:text-red-400">
                            <ul className="list-disc list-inside">
                              {fail.errors.map((error, errorIdx) => (
                                <li key={errorIdx}>{error}</li>
                              ))}
                            </ul>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Successful Uploads */}
            {newStudentUploadResults.successful && newStudentUploadResults.successful.length > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-green-600 dark:text-green-400 mb-3">
                  Successful Uploads ({newStudentUploadResults.successful.length})
                </h3>
                <div className="max-h-64 overflow-y-auto border border-green-200 dark:border-green-800 rounded-lg">
                  <table className="min-w-full divide-y divide-green-200 dark:divide-green-800">
                    <thead className="bg-green-50 dark:bg-green-900/20">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-green-800 dark:text-green-200">Row</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-green-800 dark:text-green-200">Student ID</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-green-800 dark:text-green-200">Name</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-green-800 dark:text-green-200">Email</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-green-800 dark:text-green-200">University</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-green-100 dark:divide-green-900">
                      {newStudentUploadResults.successful.map((success, idx) => (
                        <tr key={idx} className="hover:bg-green-50 dark:hover:bg-green-900/10">
                          <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">{success.row}</td>
                          <td className="px-4 py-2 text-sm">
                            <button
                              onClick={() => {
                                setShowNewStudentResults(false);
                                navigate(`/admin/students/${success.student_id}`);
                              }}
                              className="text-blue-600 dark:text-blue-400 hover:underline"
                            >
                              {success.student_id}
                            </button>
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">{success.student_name}</td>
                          <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">{success.university_email}</td>
                          <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">{success.university}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Close Button */}
            <div className="flex justify-end">
              <button
                onClick={() => setShowNewStudentResults(false)}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </AdminRouteGuard>
  );
};

export default AdminStudentRecords;
